import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';
import {
  People as PeopleIcon,
  //Psychology as PsychologyIcon,
  BarChart as ChartIcon,
  GpsFixed as AreaSearchIcon,
  MenuBook as MenuBookIcon,
  LocationOn as DwellIcon,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  Newspaper as OverwatchIcon,
  Folder as IntelPacketIcon,
} from '@mui/icons-material';
// Import the compact time slider component
import CompactTimeSlider from '../components/Pathfinder/CompactTimeSlider';
//import DigitalPoL from '../components/Pathfinder/DigitalPoL';
import InteractiveCharts, { ChartFilter } from '../components/Pathfinder/InteractiveCharts';
import PathfinderMapGL, { MapBounds } from '../components/Pathfinder/PathfinderMapGL';
import AreaSearch, { DrawCircle } from '../components/Pathfinder/AreaSearch';
import EnhancedCoTravelerAnalysis from '../components/Pathfinder/EnhancedCoTravelerAnalysis';
import DwellAnalysis from '../components/Pathfinder/DwellAnalysis';
import StoryMode from '../components/Pathfinder/StoryMode';
import GeoNeutronDataTable from '../components/Pathfinder/GeoNeutronDataTable';
import OverwatchIntel from '../components/Pathfinder/OverwatchIntel';
import IntelPacket from '../components/Pathfinder/IntelPacket';
import DeviceListSidebar from '../components/Pathfinder/DeviceListSidebar';
import NotesDialog from '../components/Pathfinder/NotesDialog';
import ForensicFlagsDialog from '../components/Pathfinder/ForensicFlagsDialog';
import geoneutronApi, { ForensicFlagData } from '../services/geoneutronApi';
import pathfinderApi, { PathfinderDataPoint, PathfinderRoute } from '../services/pathfinderApi';
import geofenceApi from '../services/geofenceApi';
import { OverwatchArticle } from '../services/overwatchApi';
import { detectDwells, identifySites, enrichSitesWithPlaceNames, updateDwellsWithSiteNames, type DwellSite } from '../utils/dwellDetection';
import { generateAutoIntel, normalizeDwellData, extractRepeatedVisitSites } from '../utils/autoIntelGeneration';
import { useDateRange } from '../contexts/DateRangeContext';
import { useTracerState, TracerState } from '../contexts/TracerContext';
import { useTracerStore } from '../stores/tracerStore';
import { useSettings } from '../contexts/SettingsContext';
import { useTheme } from '@mui/material/styles';
import { generateDeviceColor } from '../theme/entityColors';
import { useTleSatelliteLayer } from '../hooks/useSatelliteLayer';
import { tleRecordToGroundTrackFeatures, type TleRecord } from '../services/tleSatelliteService';

const EMPTY_SATELLITE_TRACKS: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

// Define MapType locally since we removed MapStylePopup import
type MapType = 'streets' | 'satellite' | 'satellite-streets' | 'dark' | 'light' | 'outdoors';

/** Normalize OS version from Explore API (e.g. "16.null" -> "16"). */
function normalizeOsVersion(v: string | null | undefined): string | null {
  const s = (v ?? '').replace(/\.null$/i, '');
  return s || null;
}


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pathfinder-tabpanel-${index}`}
      aria-labelledby={`pathfinder-tab-${index}`}
      style={{ 
        flex: 1,
        minHeight: 0, // Critical for flex child to shrink properly on scaled displays
        height: '100%', 
        display: value === index ? 'flex' : 'none',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
      {...other}
    >
      {value === index && (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `pathfinder-tab-${index}`,
    'aria-controls': `pathfinder-tabpanel-${index}`,
  };
}

const PathfinderPage: React.FC = () => {
  // Get global date range from context
  const { startDate: globalStartDate, endDate: globalEndDate, setDateRange: setGlobalDateRange } = useDateRange();
  
  // Get persistent Tracer state from context
  const { state: tracerState, updateState: updateTracerState } = useTracerState();
  
  // Get settings context for persistent map style
  const { settings, updateVisualization } = useSettings();
  
  // Get theme to determine default map style
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  const [activeTab, setActiveTab] = useState(0);
  
  // State for map and tool interactions - use context state
  const mapCenter = tracerState.mapCenter;
  const setMapCenter = (center: [number, number]) => updateTracerState({ mapCenter: center });
  const mapZoom = tracerState.mapZoom;
  const setMapZoom = (zoom: number) => updateTracerState({ mapZoom: zoom });
  
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Global filter state - applies across entire page (map, tabs, table)
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedCarriers, setSelectedCarriers] = useState<string[]>([]);
  const [selectedTimezones, setSelectedTimezones] = useState<string[]>([]);
  const [selectedForensicFlags, setSelectedForensicFlags] = useState<string[]>([]);
  
  // Filter by map bounds state - when enabled, filters table/list to only show data within visible map area
  const [filterByMapBounds, setFilterByMapBounds] = useState(false);
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);
  
  // Time-based playback state - use context
  const timeRange = tracerState.timeRange;
  const setTimeRange = (range: { start: Date; end: Date } | null) => updateTracerState({ timeRange: range });
  
  // Local state for data points and routes (fixes race conditions with Context)
  const [dataPoints, setDataPoints] = useState<PathfinderDataPoint[]>([]);
  const [visibleDataPoints, setVisibleDataPoints] = useState<PathfinderDataPoint[]>([]);
  const [routes, setRoutes] = useState<PathfinderRoute[]>([]);
  const [visibleRoutes, setVisibleRoutes] = useState<PathfinderRoute[]>([]);
  // Overwatch articles loaded on map
  const [overwatchArticles, setOverwatchArticles] = useState<OverwatchArticle[]>([]);
  
  const [showRouteLines, setShowRouteLines] = useState(false); // OFF by default
  
  // Co-traveler analysis state
  const [coTravelerResults, setCoTravelerResults] = useState<any[]>([]);
  const [coTravelerVisibility, setCoTravelerVisibility] = useState<Record<string, boolean>>({});
  
  // Data points visibility state (enabled by default)
  const [showDataPoints, setShowDataPoints] = useState(true);
  
  // Direction arrow settings
  const [showDirectionArrows, setShowDirectionArrows] = useState(false); // OFF by default
  
  // Heatmap settings
  const [showHeatmap, setShowHeatmap] = useState(false);
  
  // Clustering settings - enabled by default for better performance
  const [enableClustering, setEnableClustering] = useState(true);
  
  // Dwell locations state - use context
  const dwellLocations = tracerState.dwellLocations;
  const setDwellLocations = (dwells: any[]) => updateTracerState({ dwellLocations: dwells });
  const visibleDwells = tracerState.visibleDwells;
  const setVisibleDwells = (dwells: any[]) => updateTracerState({ visibleDwells: dwells });
  const [showDwells, setShowDwells] = useState(true); // Show dwells by default
  
  // Timeline slider visibility state
  const [timelineSliderHidden, setTimelineSliderHidden] = useState(false);

  const [saveDeviceDialogOpen, setSaveDeviceDialogOpen] = useState(false);
  const [pendingRegistrationId, setPendingRegistrationId] = useState<string>('');
  const [watchlistDeviceName, setWatchlistDeviceName] = useState('');
  const [saveDeviceLoading, setSaveDeviceLoading] = useState(false); 
  const [savedDeviceNames, setSavedDeviceNames] = useState<Map<string, string>>(() => new Map());

  const [showSatelliteLayer, setShowSatelliteLayer] = useState(false);
  const [satelliteTracksGeoJson, setSatelliteTracksGeoJson] =
    useState<GeoJSON.FeatureCollection>(EMPTY_SATELLITE_TRACKS);
  const [selectedSatelliteNorad, setSelectedSatelliteNorad] = useState<number | null>(null);
  const { geoJson: satelliteGeoJson, members: tleMembers, loading: satelliteLayerLoading } =
    useTleSatelliteLayer(showSatelliteLayer);
  const handleSatelliteTrackSelect = useCallback(
    (norad: number | null) => {
      if (norad == null || !Number.isFinite(norad)) {
        setSatelliteTracksGeoJson(EMPTY_SATELLITE_TRACKS);
        setSelectedSatelliteNorad(null);
        return;
      }
      const record = tleMembers.find(
        (m: TleRecord) => (m.norad_cat_id ?? m.satelliteId) === norad
      );
      if (!record?.line1 || !record?.line2) {
        setSatelliteTracksGeoJson(EMPTY_SATELLITE_TRACKS);
        setSelectedSatelliteNorad(null);
        return;
      }
      setSelectedSatelliteNorad(norad);
      setSatelliteTracksGeoJson({
        type: 'FeatureCollection',
        features: tleRecordToGroundTrackFeatures(record, new Date()),
      });
    },
    [tleMembers]
  );

  useEffect(() => {
    if (!showSatelliteLayer) {
      setSatelliteTracksGeoJson(EMPTY_SATELLITE_TRACKS);
      setSelectedSatelliteNorad(null);
    }
  }, [showSatelliteLayer]);
  
  // Digital PoL state
  //const [digitalPoLData, setDigitalPoLData] = useState<any[]>([]);
  //const [showDigitalPoLOnMap, setShowDigitalPoLOnMap] = useState(false);
  
  // GeoNeutron data state - use context for tabs
  const analysisTabs = tracerState.analysisTabs;
  const setAnalysisTabs = (tabs: any[] | ((prev: any[]) => any[])) => {
    if (typeof tabs === 'function') {
      updateTracerState((prev: TracerState) => ({
        analysisTabs: tabs(prev.analysisTabs)
      }));
    } else {
      updateTracerState({ analysisTabs: tabs });
    }
  };
  const [activeAnalysisTabId, setActiveAnalysisTabId] = useState<string | undefined>(undefined);
  
  // Track tab-specific data for proper cleanup when tabs are closed
  const [tabDataMap, setTabDataMap] = useState<Record<string, {
    dataPoints: PathfinderDataPoint[];
    routes: PathfinderRoute[];
  }>>({});
  
  // Track if state has been hydrated from persistence
  const [isHydrated, setIsHydrated] = useState(false);
  
  // Get Zustand store for persistence
  const zustandStore = useTracerStore();
  
  // CRITICAL: Hydrate state from Zustand on mount (restores state after navigation)
  useEffect(() => {
    if (isHydrated) return; // Only hydrate once
    
    // Check if Zustand has persisted state
    const hasPersistedData = zustandStore.dataPoints.length > 0 || zustandStore.analysisTabs.length > 0;
    
    if (hasPersistedData) {
      console.log('[Tracer] Hydrating from persisted state:', {
        dataPoints: zustandStore.dataPoints.length,
        routes: zustandStore.routes.length,
        tabs: zustandStore.analysisTabs.length
      });
      
      // Restore local state from Zustand
      setDataPoints(zustandStore.dataPoints);
      setVisibleDataPoints(zustandStore.visibleDataPoints);
      setRoutes(zustandStore.routes);
      setVisibleRoutes(zustandStore.visibleRoutes);
      setCurrentTime(zustandStore.currentTime);
      
      // Restore context state from Zustand
      updateTracerState({
        dataPoints: zustandStore.dataPoints,
        routes: zustandStore.routes,
        visibleDataPoints: zustandStore.visibleDataPoints,
        visibleRoutes: zustandStore.visibleRoutes,
        timeRange: zustandStore.timeRange,
        selectedStudents: zustandStore.selectedStudents,
        selectedDevices: zustandStore.selectedDevices,
        actualDevices: zustandStore.actualDevices,
        analysisTabs: zustandStore.analysisTabs,
        dwellLocations: zustandStore.dwellLocations,
        visibleDwells: zustandStore.visibleDwells,
        mapCenter: zustandStore.mapCenter,
        mapZoom: zustandStore.mapZoom,
        drawCircles: zustandStore.drawCircles,
      });
      
      // Rebuild tabDataMap from analysisTabs
      if (zustandStore.analysisTabs.length > 0) {
        const rebuiltTabDataMap: Record<string, { dataPoints: PathfinderDataPoint[]; routes: PathfinderRoute[] }> = {};
        zustandStore.analysisTabs.forEach(tab => {
          const tabPoints = tab.polDataPoints || (tab as any).areaDataPoints || [];
          const tabRoutes = (tab as any).areaRoutes || pathfinderApi.generateRoutes(tabPoints);
          rebuiltTabDataMap[tab.id] = { dataPoints: tabPoints, routes: tabRoutes };
        });
        setTabDataMap(rebuiltTabDataMap);
        
        // Set active tab to first tab
        setActiveAnalysisTabId(zustandStore.analysisTabs[0].id);
      }
    }
    
    setIsHydrated(true);
  }, [isHydrated, zustandStore, updateTracerState]);
  
  // CRITICAL: Persist state to Zustand when it changes (for navigation preservation)
  useEffect(() => {
    if (!isHydrated) return; // Don't persist during hydration
    
    // Debounce persistence to avoid excessive writes
    const timeoutId = setTimeout(() => {
      zustandStore.updateState({
        dataPoints,
        routes,
        visibleDataPoints,
        visibleRoutes,
        timeRange,
        currentTime,
        selectedStudents: tracerState.selectedStudents,
        selectedDevices: tracerState.selectedDevices,
        actualDevices: tracerState.actualDevices,
        analysisTabs,
        dwellLocations,
        visibleDwells,
        mapCenter,
        mapZoom,
        drawCircles: tracerState.drawCircles,
      });
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [
    isHydrated,
    dataPoints,
    routes,
    visibleDataPoints,
    visibleRoutes,
    timeRange,
    currentTime,
    analysisTabs,
    dwellLocations,
    visibleDwells,
    mapCenter,
    mapZoom,
    tracerState.selectedStudents,
    tracerState.selectedDevices,
    tracerState.actualDevices,
    tracerState.drawCircles,
    zustandStore
  ]);
  
  // Critical Infrastructure proximity findings (airports, seaports, borders)
  const [infrastructureFindings, setInfrastructureFindings] = useState<Array<{
    point_id: string;
    lat: number;
    lon: number;
    infrastructure: Array<{
      id: number;
      name: string;
      type: string;
      icon: string;
      distance_meters: number;
    }>;
  }>>([]);
  
  // Infrastructure notification snackbar
  const [infrastructureAlert, setInfrastructureAlert] = useState<{
    open: boolean;
    message: string;
    details: string[];
  }>({ open: false, message: '', details: [] });
  
  // Filter function to apply metadata filters to data points
  const applyMetadataFilters = useCallback((points: PathfinderDataPoint[]): PathfinderDataPoint[] => {
    let filtered = points;

    // Apply country filter
    if (selectedCountries.length > 0) {
      filtered = filtered.filter(point => 
        point.metadata?.country && selectedCountries.includes(point.metadata.country)
      );
    }

    // Apply carrier filter
    if (selectedCarriers.length > 0) {
      filtered = filtered.filter(point =>
        point.metadata?.carrier && selectedCarriers.includes(point.metadata.carrier)
      );
    }

    // Apply timezone filter
    if (selectedTimezones.length > 0) {
      filtered = filtered.filter(point =>
        point.metadata?.timezone && selectedTimezones.includes(point.metadata.timezone)
      );
    }

    // Apply forensic flags filter
    if (selectedForensicFlags.length > 0) {
      filtered = filtered.filter(point =>
        point.metadata?.forensic_flag && selectedForensicFlags.includes(point.metadata.forensic_flag)
      );
    }

    return filtered;
  }, [selectedCountries, selectedCarriers, selectedTimezones, selectedForensicFlags]);

  // Helper function to add a new analysis tab
  const addAnalysisTab = useCallback((newTab: any) => {
    setAnalysisTabs(prev => [...prev, newTab]);
    setActiveAnalysisTabId(newTab.id);
  }, [setAnalysisTabs]);

  // Sync visible data with active analysis tab
  // IMPORTANT: Uploaded data is ALWAYS visible, merged with active tab's data
  useEffect(() => {
    if (analysisTabs.length === 0) {
      // Clear data if no tabs
      setVisibleDataPoints([]);
      setRoutes([]);
      setVisibleRoutes([]);
      setDwellLocations([]);
      setDwellSites([]);
      return;
    }

    if (!activeAnalysisTabId) {
      // Default to first tab if none active
      if (analysisTabs.length > 0) {
         setActiveAnalysisTabId(analysisTabs[0].id);
      }
      return;
    }

    const activeTab = analysisTabs.find(t => t.id === activeAnalysisTabId);
    if (!activeTab) return;

    // Gather data from ALL alwaysVisible tabs (not just 'uploaded-data')
    let alwaysVisiblePoints: PathfinderDataPoint[] = [];
    let alwaysVisibleRoutes: PathfinderRoute[] = [];
    analysisTabs.forEach(tab => {
      if (tab.alwaysVisible && tab.id !== activeAnalysisTabId) {
        const td = tabDataMap[tab.id];
        const pts = td?.dataPoints || tab.polDataPoints || [];
        const rts = td?.routes || pathfinderApi.generateRoutes(pts);
        alwaysVisiblePoints.push(...pts);
        alwaysVisibleRoutes.push(...rts);
      }
    });

    const tabData = tabDataMap[activeAnalysisTabId];
    
    let activeTabPoints = tabData?.dataPoints || activeTab.polDataPoints || (activeTab as any).areaDataPoints || [];
    let activeTabRoutes = tabData?.routes || (activeTab as any).areaRoutes || pathfinderApi.generateRoutes(activeTabPoints);
    
    // Merge: always-visible tabs' data + active tab's data
    let dataPoints: PathfinderDataPoint[] = [...alwaysVisiblePoints, ...activeTabPoints];
    let routes: PathfinderRoute[] = [...alwaysVisibleRoutes, ...activeTabRoutes];
    
    if (dataPoints.length > 0) {
      // Apply metadata filters
      const filteredPoints = applyMetadataFilters(dataPoints);
      // Update global context data points to active tab only
      setDataPoints(dataPoints); // Keep unfiltered for reference
      setVisibleDataPoints(filteredPoints); // Show filtered
      setRoutes(routes);
      setVisibleRoutes(routes);
      
      // ⚠️ CRITICAL: Update TracerContext when switching tabs so Link Analysis works
      updateTracerState({ 
        dataPoints: dataPoints,
        routes: routes 
      });
      
      // Clear session-specific states to prevent data bleed
      setCoTravelerResults([]);
      
      // ⚠️ CRITICAL: Build selectedDevices from the loaded data points for co-traveler analysis
      const deviceMap: Record<string, any[]> = {};
      const selectedDeviceMap: Record<string, string[]> = {};
      const devicePointCounts = new Map<string, number>();
      
      // Count points per device for display
      dataPoints.forEach((point: PathfinderDataPoint) => {
        const studentId = point.studentId || point.deviceId;
        const deviceType = (point.deviceType || 'phone').toLowerCase();
        const uniqueDeviceId = `${studentId}-${deviceType}`;
        devicePointCounts.set(uniqueDeviceId, (devicePointCounts.get(uniqueDeviceId) || 0) + 1);
      });
      
      const processedDevices = new Set<string>();
      dataPoints.forEach((point: PathfinderDataPoint) => {
        const studentId = point.studentId || point.deviceId;
        const deviceType = (point.deviceType || 'phone').toLowerCase();
        const uniqueDeviceId = `${studentId}-${deviceType}`;
        
        if (!processedDevices.has(uniqueDeviceId)) {
          processedDevices.add(uniqueDeviceId);
          
          if (!deviceMap[studentId]) {
            deviceMap[studentId] = [];
            selectedDeviceMap[studentId] = [];
          }
          
          const pointCount = devicePointCounts.get(uniqueDeviceId) || 0;
          const device = {
            id: uniqueDeviceId,
            type: deviceType,
            name: `${deviceType.charAt(0).toUpperCase() + deviceType.slice(1)}`,
            active: true,
            lastSeen: point.timestamp,
            coordinates: point.coordinates,
            dataPointCount: pointCount,
          };
          
          deviceMap[studentId].push(device);
          selectedDeviceMap[studentId].push(uniqueDeviceId); // Auto-select all devices
        }
      });
      
      setActualDevices(deviceMap);
      setSelectedDevices(selectedDeviceMap);
      
      // Handle Dwells and Sites
      let currentDwells: any[] = [];
      if (activeTab.dwellLocations && activeTab.dwellLocations.length > 0) {
         currentDwells = activeTab.dwellLocations;
      }
      setDwellLocations(currentDwells);
      setVisibleDwells(currentDwells); // Also update visible dwells for map
      
      // ⚠️ CRITICAL: Sync dwellLocations to TracerContext for Link Analysis
      updateTracerState({ dwellLocations: currentDwells });
      
      // Calculate sites for the active tab
      if (currentDwells.length > 0) {
         const { sites } = identifySites(currentDwells, 150, dataPoints);
         setDwellSites(sites);
         
         // Auto-generate Pattern of Life intel for saved devices
         // This runs asynchronously and won't block the UI
         (async () => {
           try {
             const normalizedDwells = normalizeDwellData(currentDwells);
             const repeatedVisitSites = extractRepeatedVisitSites(normalizedDwells);
             
             const result = await generateAutoIntel(
               normalizedDwells,
               [], // Co-traveler results will be added separately when analysis runs
               repeatedVisitSites,
               {
                 minDwellDurationMinutes: 30, // Only log dwells > 30 min
                 minRepeatedVisits: 2,        // Only log sites visited 2+ times
               }
             );
             
             if (result.created > 0) {
               console.log(`[Auto-Intel] Generated ${result.created} new PoL entries (${result.skipped} skipped, ${result.errors} errors)`);
             }
           } catch (error) {
             console.error('[Auto-Intel] Error generating PoL intel:', error);
           }
         })();
      } else {
         setDwellSites([]);
      }
      
      if (dataPoints.length > 0) {
        const timestamps = dataPoints.map(p => p.timestamp);
        const minTime = new Date(Math.min(...timestamps.map(t => t.getTime())));
        const maxTime = new Date(Math.max(...timestamps.map(t => t.getTime())));
        
        // Update both local and global time range
        const newRange = { start: minTime, end: maxTime };
        setTimeRange(newRange);
        setCurrentTime(maxTime);
        
        // ⚠️ CRITICAL: Update TracerContext time range
        updateTracerState({ timeRange: newRange });
      }
      
      // Handle Map Center - skip if we're in the middle of a programmatic zoom
      if (activeTab.circleInfo && !skipMapChangeRef.current) {
         setMapCenter(activeTab.circleInfo.center);
      }

      // Handle Selected Students (for Story Mode, Dwell filtering, and Co-Traveler Analysis)
      // Use the students derived from device map building for consistency
      const studentsFromDeviceMap = Object.keys(selectedDeviceMap);
      if (studentsFromDeviceMap.length > 0) {
         setSelectedStudents(studentsFromDeviceMap);
      } else if (activeTab.deviceId) {
         // Fallback: Split comma-separated device IDs
         const deviceIds = activeTab.deviceId.includes(',') 
           ? activeTab.deviceId.split(',').map((id: string) => id.trim())
           : [activeTab.deviceId];
         setSelectedStudents(deviceIds);
      } else {
         setSelectedStudents([]);
      }
      
      
      // Zoom map to fit the data for this tab - check skip flag INSIDE the timeout
      // (because the zoom might be triggered after we schedule this)
      requestAnimationFrame(() => {
        setTimeout(() => {
          // Check skip flag at execution time, not at scheduling time
          if (!skipMapChangeRef.current) {
            zoomToFitData(filteredPoints, currentDwells);
          }
        }, 150);
      });
    } else {
      // Clear map when switching to empty tab
      setVisibleDataPoints([]);
      setVisibleRoutes([]);
    }
  }, [activeAnalysisTabId, analysisTabs, tabDataMap, applyMetadataFilters]);
  
  // Helper function to remove a tab
  const removeAnalysisTab = (tabId: string) => {
    
    // Find the tab being removed to check if it's a CSV tab
    const tabToRemove = analysisTabs.find(tab => tab.id === tabId);
    
    // Remove the tab from the list
    const updatedTabs = analysisTabs.filter(tab => tab.id !== tabId);
    setAnalysisTabs(updatedTabs);
    
    // Remove the tab's data from the map
    const updatedTabDataMap = { ...tabDataMap };
    delete updatedTabDataMap[tabId];
    setTabDataMap(updatedTabDataMap);
    
    // If it was the Uploaded Data tab, notify the CSV dialog to clear all files
    if (tabId === 'uploaded-data') {
      window.dispatchEvent(new CustomEvent('csv-file-unloaded', { 
        detail: { clearAll: true }
      }));
    } else if (tabToRemove?.type === 'csv' && tabToRemove.uploadedFiles?.length) {
  tabToRemove.uploadedFiles.forEach((fileName: string) => {
    window.dispatchEvent(new CustomEvent('csv-file-unloaded', { 
      detail: { fileName }
    }));
  });
}
    
    // If we're closing the active tab, switch to another tab or clear
    if (activeAnalysisTabId === tabId) {
      if (updatedTabs.length > 0) {
        setActiveAnalysisTabId(updatedTabs[0].id);
      } else {
        setActiveAnalysisTabId(undefined);
      }
    }
    
    // Recalculate visible data from remaining tabs
    const allRemainingPoints: PathfinderDataPoint[] = [];
    const allRemainingRoutes: PathfinderRoute[] = [];
    
    Object.values(updatedTabDataMap).forEach(tabData => {
      allRemainingPoints.push(...tabData.dataPoints);
      allRemainingRoutes.push(...tabData.routes);
    });
    
    // Update the map with remaining data
    setDataPoints(allRemainingPoints);
    setRoutes(allRemainingRoutes);
    setVisibleDataPoints(allRemainingPoints);
    setVisibleRoutes(allRemainingRoutes);
    
    // Update TracerContext to keep Link Analysis in sync
    updateTracerState({ 
      dataPoints: allRemainingPoints,
      routes: allRemainingRoutes 
    });
    
    // If no data remains, also clear dwells and other state
    if (allRemainingPoints.length === 0) {
      setDwellLocations([]);
      setVisibleDwells([]);
      setDwellSites([]);
      setSelectedStudents([]);
      setActualDevices({});
      setSelectedDevices({});
      setTimeRange(null);
      updateTracerState({ 
        dwellLocations: [],
        timeRange: null 
      });
    }
    
    // If no more tabs, clear everything and reset panel state
    if (updatedTabs.length === 0) {
      setSelectedStudents([]);
      setActualDevices({});
      setSelectedDevices({});
      setTimeRange(null);
      // Reset left panel collapsed state to prevent white bar artifact
      setLeftPanelCollapsed(false);
      // Force map resize after panel disappears
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 50);
    }
  };
  
  // Map type settings - use persisted setting from context
  const mapType = (settings.visualization.mapStyle || (isDarkMode ? 'dark' : 'streets')) as MapType;
  
  // Update map style when theme changes (only if user hasn't explicitly set one)
  useEffect(() => {
    // Only auto-update if the current style matches the opposite theme's default
    // This preserves user's explicit choice while still responding to theme changes
    if (settings.visualization.mapStyle === 'streets' && isDarkMode) {
      updateVisualization({ mapStyle: 'dark' });
    } else if (settings.visualization.mapStyle === 'dark' && !isDarkMode) {
      updateVisualization({ mapStyle: 'streets' });
    }
  }, [isDarkMode, settings.visualization.mapStyle, updateVisualization]);
  
  // Trail length removed - feature was causing issues
  
  // Follow mode for camera tracking during playback
  const [followMode, setFollowMode] = useState(false);
  
  // Trigger for extent updates (increment to trigger re-fit)
  const [_extentUpdateTrigger, setExtentUpdateTrigger] = useState(0);
  
  // CSV Upload state - removed unused dialog state (now handled in Layout.tsx)
  
  // Data state (matching Analysis tab pattern with database tags)
  const [selectedCourse, _setSelectedCourse] = useState<string | undefined>();
  
  // Selected students and devices - use context
  const selectedStudents = tracerState.selectedStudents;
  const setSelectedStudents = (students: string[]) => updateTracerState({ selectedStudents: students });
  const selectedDevices = tracerState.selectedDevices;
  const setSelectedDevices = (devices: Record<string, string[]>) => updateTracerState({ selectedDevices: devices });
  const setActualDevices = (devices: Record<string, any[]>) => updateTracerState({ actualDevices: devices });
  
  // Data points - use local state defined above
  // const dataPoints = tracerState.dataPoints;
  // const setDataPoints = (points: PathfinderDataPoint[]) => updateTracerState({ dataPoints: points });

  // Highlight location for map (used when clicking on PoL items)
  const [highlightLocation, setHighlightLocation] = useState<{ lat: number; lng: number } | null>(null);
  
  // Flag to skip onMapChange during programmatic zooms (prevents flyTo animation from overwriting intended zoom)
  const skipMapChangeRef = useRef(false);
  
  // Store original map position before zooming to a location (for "zoom back" feature)
  const originalMapViewRef = useRef<{ center: [number, number]; zoom: number } | null>(null);

  // Right panel state
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const [rightPanelWidth, setRightPanelWidth] = useState(600);
  const [isResizing, setIsResizing] = useState(false);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [leftPanelWidth, setLeftPanelWidth] = useState(400);
  const [isLeftResizing, setIsLeftResizing] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [polLoading, setPolLoading] = useState(false);
  const [polLoadingMessage, setPolLoadingMessage] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('');
  
  // Use global date range from context - memoized to prevent infinite re-renders
  const dateRange = useMemo(() => ({
    start: globalStartDate,
    end: globalEndDate
  }), [globalStartDate, globalEndDate]);

  // Area search / circle drawing state - use context
  const drawCircles = tracerState.drawCircles;
  const setDrawCircles = (circlesOrUpdater: DrawCircle[] | ((prev: DrawCircle[]) => DrawCircle[]))  => {
    if (typeof circlesOrUpdater === 'function') {
      // Use functional update to avoid stale closures
      updateTracerState((prev: TracerState) => ({
        drawCircles: circlesOrUpdater(prev.drawCircles)
      }));
    } else {
      updateTracerState({ drawCircles: circlesOrUpdater });
    }
  };
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [drawingShapeType, setDrawingShapeType] = useState<'circle' | 'polygon'>('circle');
  const [defaultRadius, setDefaultRadius] = useState(500);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Right panel resize handlers
  const handleRightPanelToggle = () => {
    setRightPanelCollapsed(!rightPanelCollapsed);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };


  // Trigger window resize event when panel collapses/expands to update map
  useEffect(() => {
    // Trigger resize immediately
    window.dispatchEvent(new Event('resize'));
    
    // Also trigger after CSS transition completes (300ms transition + buffer)
    const timer1 = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
    
    const timer2 = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 350);
    
    // Extra trigger for slow transitions
    const timer3 = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 500);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [rightPanelCollapsed, rightPanelWidth, leftPanelCollapsed]);

  // Right panel resize handler
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      
      const newWidth = window.innerWidth - e.clientX;
      if (newWidth >= 300 && newWidth <= 800) {
        setRightPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Left panel resize handler
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isLeftResizing) return;
      
      const newWidth = e.clientX;
      if (newWidth >= 280 && newWidth <= 600) {
        setLeftPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsLeftResizing(false);
      // Trigger map resize after panel resize completes
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 50);
    };

    if (isLeftResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isLeftResizing]);

  // Area search / circle drawing handlers
  const handleDrawModeChange = useCallback((enabled: boolean, shapeType?: 'circle' | 'polygon') => {
    setIsDrawingMode(enabled);
    if (shapeType) {
      setDrawingShapeType(shapeType);
    }
  }, [drawingShapeType]);

  const handleCircleAdd = useCallback((circle: DrawCircle) => {
    setDrawCircles(prev => [...prev, circle]);
  }, [setDrawCircles]);

  const handleCircleUpdate = useCallback((updatedCircle: DrawCircle) => {
    setDrawCircles(prev => prev.map(circle => 
      circle.id === updatedCircle.id ? updatedCircle : circle
    ));
  }, [setDrawCircles]);

  // Handle partial circle updates (for editing)
  const handleCirclePartialUpdate = useCallback(async (circleId: string, updates: Partial<DrawCircle>) => {
    
    // Find the circle being updated
    const circle = drawCircles.find(c => c.id === circleId);
    if (!circle) return;
    
    // Update local state
    const updatedCircle = { ...circle, ...updates };
    setDrawCircles(prev => prev.map(c => 
      c.id === circleId ? updatedCircle : c
    ));
    
    // If this is a saved geofence, update it in the database
    if (circle.geofenceId && (updates.center || updates.radius || updates.coordinates)) {
      try {
        
        let geometry;
        if (updatedCircle.shapeType === 'polygon' && updatedCircle.coordinates) {
          geometry = {
            type: 'Polygon' as const,
            coordinates: updatedCircle.coordinates
          };
        } else {
          // Convert circle to polygon
          // center is [lat, lon] but circleToPolygon expects [lng, lat] (GeoJSON format)
          const finalCenter = updates.center || circle.center;
          const finalRadius = updates.radius || circle.radius;
          geometry = geofenceApi.circleToPolygon([finalCenter[1], finalCenter[0]], finalRadius);
        }
        
        await geofenceApi.updateGeofence(circle.geofenceId, {
          geometry,
          description: updatedCircle.shapeType === 'circle' 
            ? `Circle: ${updatedCircle.radius}m radius at ${updatedCircle.center[0].toFixed(5)}, ${updatedCircle.center[1].toFixed(5)}`
            : `Polygon with ${updatedCircle.coordinates?.[0]?.length || 0} vertices`
        });
        
      } catch (error) {
        // Don't show alert - just log the error so editing continues to work
      }
    }
  }, [drawCircles, setDrawCircles]);

  // State for tracking which circle is being edited
  const [editingCircleId, setEditingCircleId] = useState<string | null>(null);

  const handleCircleDelete = useCallback((circleId: string) => {
    setDrawCircles(prev => prev.filter(circle => circle.id !== circleId));
  }, [setDrawCircles]);

  // Handle Digital PoL data update
  /*const handleDigitalPoLDataChange = useCallback((data: any[], showOnMap: boolean) => {
    setDigitalPoLData(data);
    setShowDigitalPoLOnMap(showOnMap);
  }, []);*/

  const handleCircleDrawn = useCallback((center: [number, number], radius: number, coordinates?: number[][][]) => {
    // If coordinates are provided, it's definitely a polygon (from Mapbox Draw)
    const shapeType = coordinates ? 'polygon' : drawingShapeType;
    
    const newShape: DrawCircle = {
      id: `${shapeType}-${Date.now()}`,
      shapeType: shapeType,
      center,
      radius,
      coordinates, // For polygons
      name: `Area ${drawCircles.length + 1}`,
      visible: true,
      results: [],
      loading: false,
    };
    handleCircleAdd(newShape);
    setIsDrawingMode(false); // Exit drawing mode after creating shape
  }, [drawCircles.length, drawingShapeType, handleCircleAdd]);

  // Handle loading dwells from area search
  const handleLoadAreaDwells = useCallback((areaDwells: any[]) => {
    
    if (areaDwells.length === 0) {
      return;
    }
    
    // Convert DwellInsight format to dwell location format for map (red circles)
    // DwellInsight coordinates are in GeoJSON format: [lng, lat]
    const dwellLocationsData = areaDwells.map((insight, idx) => {
      const dwellLocation = {
        id: idx,
        latitude: insight.coordinates[1],         // DwellInsight uses GeoJSON: [lng, lat]
        longitude: insight.coordinates[0],        // DwellInsight uses GeoJSON: [lng, lat]
        center_latitude: insight.coordinates[1],  // Keep for compatibility
        center_longitude: insight.coordinates[0], // Keep for compatibility
        total_visits: insight.dwellCount,
        total_duration: insight.totalDwellTime,
        average_duration: insight.totalDwellTime / insight.dwellCount,
        duration_seconds: insight.totalDwellTime,
        place_name: insight.placeName || 'Unknown Location',
        place_type: insight.placeType || 'unknown',
        students: [insight.entityId],
        entity_id: insight.entityId,
        device_type: insight.deviceType,
        radius: 50,  // Default radius for dwell circle display
        // Add start_time and end_time for time-based filtering
        start_time: insight.longestDwell.startTime.toISOString(),
        end_time: insight.longestDwell.endTime.toISOString()
      };
      
      if (idx === 0) {
      }
      
      return dwellLocation;
    });
    
    setDwellLocations(dwellLocationsData);
    setVisibleDwells(dwellLocationsData);  // Set visible dwells too (what map actually uses)
    setShowDwells(true);  // Ensure dwells are visible
  }, []);

  // Check infrastructure proximity for loaded data points (airports, seaports, borders)
  const checkInfrastructureProximity = useCallback(async (points: PathfinderDataPoint[]) => {
    if (points.length === 0) return;
    
    try {
      // Sample points to avoid overwhelming the API (check unique locations)
      const uniqueLocations = new Map<string, { lat: number; lon: number; id: string }>();
      points.forEach(point => {
        // Coordinates are in GeoJSON format: [lng, lat]
        const lng = point.coordinates[0];
        const lat = point.coordinates[1];
        const key = `${lng.toFixed(3)},${lat.toFixed(3)}`;
        if (!uniqueLocations.has(key)) {
          uniqueLocations.set(key, {
            lat: lat,
            lon: lng,
            id: point.id || `${point.deviceId}-${point.timestamp}`
          });
        }
      });
      
      // Limit to 100 unique locations to avoid API overload
      const locationsToCheck = Array.from(uniqueLocations.values()).slice(0, 100);
      
      if (locationsToCheck.length === 0) return;
      
      const result = await pathfinderApi.checkInfrastructureProximityBatch({
        points: locationsToCheck,
        radius_meters: 1500, // 1.5km radius for infrastructure detection
        types: ['airport', 'seaport', 'border_crossing']
      });
      
      // API returns results as object keyed by point_id, not array
      const resultsObj = result.results || {};
      const pointsWithInfra = Object.keys(resultsObj).length;
      
      if (pointsWithInfra > 0) {
        // Convert object to array format for state
        // The infrastructure value is already the array of infrastructure items
        type InfraItem = {
          id: number;
          name: string;
          type: string;
          icon: string;
          distance_meters: number;
        };
        
        const nearbyPoints = Object.entries(resultsObj).map(([pointId, infraList]) => ({
          point_id: pointId,
          lat: 0,
          lon: 0,
          infrastructure: (infraList as unknown) as InfraItem[]
        }));
        
        setInfrastructureFindings(nearbyPoints);
        
        // Build infrastructure summary for popup
        const infrastructureTypes = new Map<string, Set<string>>();
        nearbyPoints.forEach(point => {
          point.infrastructure.forEach((infra) => {
            if (!infrastructureTypes.has(infra.type)) {
              infrastructureTypes.set(infra.type, new Set());
            }
            infrastructureTypes.get(infra.type)!.add(infra.name);
          });
        });
        
        // Count total unique infrastructure locations
        let totalInfraLocations = 0;
        infrastructureTypes.forEach((names) => {
          totalInfraLocations += names.size;
        });
        
        // Create details list
        const details: string[] = [];
        infrastructureTypes.forEach((names, type) => {
          const typeIcon = type === 'airport' ? '✈️' : type === 'seaport' ? '🚢' : '🛂';
          const typeName = type === 'airport' ? 'Airports' : type === 'seaport' ? 'Seaports' : 'Border Crossings';
          const namesList = Array.from(names).slice(0, 3).join(', ');
          const more = names.size > 3 ? ` +${names.size - 3} more` : '';
          details.push(`${typeIcon} ${typeName}: ${namesList}${more}`);
        });
        
        // Show infrastructure alert
        setInfrastructureAlert({
          open: true,
          message: `⚠️ Device activity found near ${totalInfraLocations} transportation hub${totalInfraLocations > 1 ? 's' : ''}!`,
          details
        });
      } else {
        setInfrastructureFindings([]);
      }
    } catch (error) {
      console.error('Infrastructure proximity check failed:', error);
    }
  }, []);

  // Handle loading data for entities found in area search
  const handleLoadAreaData = useCallback(async (areaDataPoints: PathfinderDataPoint[]) => {
        
        // Debug coordinate format
        
        // Ensure coordinates are in the right format - internal system uses [lat, lng]
        const validatedDataPoints = areaDataPoints.map(point => {
          if (!point.coordinates || !Array.isArray(point.coordinates) || point.coordinates.length < 2) {
            return null;
          }
          
          // Coordinates are in internal format [lat, lng]
          const lat = parseFloat(String(point.coordinates[0]));
          const lng = parseFloat(String(point.coordinates[1]));
          
          if (isNaN(lat) || isNaN(lng)) {
            return null;
          }
          
          return {
            ...point,
            coordinates: [lat, lng] as [number, number]  // Keep internal format [lat, lng]
          };
        }).filter(p => p !== null) as PathfinderDataPoint[];
        
        
        // Extract unique entity IDs from the data points
        const entityIds = Array.from(new Set(areaDataPoints.map(p => p.studentId || p.deviceId)));
    
    // Update selected students to include the found entities
    setSelectedStudents(entityIds);
    
    // Use the validated data points directly from the area search
    setLoading(true);
    setLoadingMessage('Processing area search data...');
    try {
      // Use the validated data points from the area search
      setDataPoints(validatedDataPoints);
      
      
      // Process into routes
      const newRoutes = pathfinderApi.generateRoutes(validatedDataPoints);
      setRoutes(newRoutes);
      
      // ⚠️ CRITICAL: Also update TracerContext so Link Analysis page can access the data
      updateTracerState({ 
        dataPoints: validatedDataPoints,
        routes: newRoutes 
      });
      
      // Extract devices and auto-select them
      const deviceMap: Record<string, any[]> = {};
      const selectedDeviceMap: Record<string, string[]> = {};
      
      // Build device map from the validated points
      // First, count points per device
      const devicePointCounts = new Map<string, number>();
      validatedDataPoints.forEach(point => {
        const studentId = point.studentId || point.deviceId;
        const deviceType = (point.deviceType || 'phone').toLowerCase();
        const uniqueDeviceId = `${studentId}-${deviceType}`;
        devicePointCounts.set(uniqueDeviceId, (devicePointCounts.get(uniqueDeviceId) || 0) + 1);
      });
      
      const processedDevices = new Set<string>();
      validatedDataPoints.forEach(point => {
          const studentId = point.studentId || point.deviceId;
          const deviceType = (point.deviceType || 'phone').toLowerCase();
          const uniqueDeviceId = `${studentId}-${deviceType}`;
          
          if (!processedDevices.has(uniqueDeviceId)) {
            processedDevices.add(uniqueDeviceId);
            
            if (!deviceMap[studentId]) {
              deviceMap[studentId] = [];
              selectedDeviceMap[studentId] = [];
            }
            
            const pointCount = devicePointCounts.get(uniqueDeviceId) || 0;
            const device = {
              id: uniqueDeviceId,
              type: deviceType,
              name: `${deviceType.charAt(0).toUpperCase() + deviceType.slice(1)}`,
              active: true,
              color: deviceType === 'watch' ? theme.palette.info.main : deviceType === 'vehicle' ? theme.palette.secondary.main : theme.palette.success.main,
              lastSeen: point.timestamp,
              coordinates: point.coordinates,
              dataPointCount: pointCount,
              totalDataPoints: validatedDataPoints.length, // Total across all devices
            };
            
            
            deviceMap[studentId].push(device);
            selectedDeviceMap[studentId].push(uniqueDeviceId); // Auto-select all devices
          }
        });
        
      // Set the devices and selection
      setActualDevices(deviceMap);
      setSelectedDevices(selectedDeviceMap);
      
      // Initialize selectedStudents based on which students have devices selected
      const studentsWithSelectedDevices = Object.keys(selectedDeviceMap).filter(
        studentId => selectedDeviceMap[studentId].length > 0
      );
      setSelectedStudents(studentsWithSelectedDevices);
      
      // Set time range if we have validated data
      if (validatedDataPoints.length > 0) {
        const timestamps = validatedDataPoints.map((p: PathfinderDataPoint) => p.timestamp);
        const minTime = new Date(Math.min(...timestamps.map((t: Date) => t.getTime())));
        const maxTime = new Date(Math.max(...timestamps.map((t: Date) => t.getTime())));
        const newTimeRange = { start: minTime, end: maxTime };
        setTimeRange(newTimeRange);
        setCurrentTime(maxTime); // Set current time to end to show all data
        
        // ⚠️ CRITICAL: Also update TracerContext timeRange for Link Analysis
        updateTracerState({ timeRange: newTimeRange });
        
        // Force visible data update
        setVisibleDataPoints(validatedDataPoints); // Show validated points immediately
        setVisibleRoutes(newRoutes); // Show all routes immediately
        
        
        // Force map to zoom to show the loaded data
        setTimeout(() => {
          // The useEffect in PathfinderMapGL will handle the bounds fitting
        }, 100);
      }
      
    } catch (error) {
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }, []);

  const loadCoTravelerTracks = useCallback(async (coTravelerResults: any[]) => {
    try {
      // Extract unique co-traveler device IDs from the analysis results
      const coTravelerDeviceIds = Array.from(new Set(
        coTravelerResults.map(result => result.coTravelerDevice)
      ));
      
      
      if (coTravelerDeviceIds.length === 0) {
        return;
      }
      
      // Extract entity IDs from device IDs (e.g., "Student-07 - Phone" -> "Student-07")
      const coTravelerEntityIds = Array.from(new Set(
        coTravelerDeviceIds.map(deviceId => {
          
          // IMPORTANT: For Students, we need the entity ID (e.g., "Student-12")
          // not the device ID (e.g., "Student-12-phone")
          
          // Handle different device ID formats with spaces: "Student-04 - Vehicle", "RP-48 - Phone"
          if (deviceId.includes(' - ')) {
            const entityId = deviceId.split(' - ')[0]; // Take everything before " - "
            return entityId;
          }
          
          // Handle old format without spaces: "Student-07-phone"
          if (deviceId.includes('-phone') || deviceId.includes('-watch') || deviceId.includes('-vehicle')) {
            const entityId = deviceId.split('-').slice(0, -1).join('-');
            return entityId;
          }
          
          // If it starts with "Student-" but has no device suffix, it's already an entity ID
          if (deviceId.startsWith('Student-')) {
            return deviceId;
          }
          
          // For entities that don't have device suffixes (like pure SRP, RP)
          return deviceId;
        })
      ));
      
      
      // CRITICAL: Query GPS data for co-traveler ENTITIES using the FULL selected date range
      // This MUST load ALL GPS points for the entire timespan, not just encounter areas
      // We want COMPLETE tracks for full Pattern of Life playback
      const coTravelerQuery = {
        dateRange: [dateRange.start!, dateRange.end!] as [Date, Date],
        studentIds: coTravelerEntityIds, // Use entity IDs to get ALL their data
        selectedCourse: selectedCourse,
        deviceTypes: ['phone', 'vehicle', 'watch'], // Load ALL device types for full PoL
        limit: undefined, // NO LIMIT - load all GPS points for entire timespan
        // IMPORTANT: This is a SEPARATE query from co-traveler identification
        // This loads COMPLETE tracks, not filtered by proximity to primary subject
      };
      
      
      // Add detailed debugging for GPS data query
      
      
      const coTravelerPointsRaw = await pathfinderApi.queryDataPoints(coTravelerQuery);
      
      // Analyze the actual date range of received data
      if (coTravelerPointsRaw.length > 0) {
        const timestamps = coTravelerPointsRaw.map(p => p.timestamp.getTime());
        const actualStart = new Date(Math.min(...timestamps));
        const actualEnd = new Date(Math.max(...timestamps));
        
        // Check if we got the full range
        const requestedStart = coTravelerQuery.dateRange[0].getTime();
        const requestedEnd = coTravelerQuery.dateRange[1].getTime();
        const receivedStart = actualStart.getTime();
        const receivedEnd = actualEnd.getTime();
        
        if (receivedStart > requestedStart + 86400000 || receivedEnd < requestedEnd - 86400000) {
        }
      }
      
      // DEBUG: Check if SRP-17 has data
      const srp17Data = coTravelerPointsRaw.filter(p => p.studentId === 'SRP-17');
      if (srp17Data.length > 0) {
      }
      
      // FILTER CO-TRAVELER GPS TRACKS: Exclude points IN Davis-Monthan AFB
      // This is DISPLAY filtering - we already found co-travelers based on encounters OUTSIDE the base,
      // but now we need to hide their GPS tracks when they were IN the base (matching behavior)
      const davisMonthanPolygon: [number, number][] = [
        [32.1899942, -110.8668227],  // [lat, lon] format
        [32.1759060, -110.8666880],
        [32.1804096, -110.8931131],
        [32.1944209, -110.8933825]
      ];
      
      const isPointInDavisMonthan = (lat: number, lon: number): boolean => {
        // Using correct coordinate system: lon=x, lat=y
        const x = lon;
        const y = lat;
        let inside = false;
        for (let i = 0, j = davisMonthanPolygon.length - 1; i < davisMonthanPolygon.length; j = i++) {
          const [yi, xi] = davisMonthanPolygon[i];  // polygon is [lat, lon]
          const [yj, xj] = davisMonthanPolygon[j];
          const intersect = ((yi > y) !== (yj > y)) && 
                           (x < (xj - xi) * (y - yi) / (yj - yi + Number.EPSILON) + xi);
          if (intersect) inside = !inside;
        }
        return inside;
      };
      
      // EXCLUSION TOGGLE: Set to false to disable display filtering for testing
      // TO RE-ENABLE: Change both ENABLE_DISPLAY_EXCLUSION here and ENABLE_EXCLUSION_ZONE in pathfinderApi.ts to true
      const ENABLE_DISPLAY_EXCLUSION = false; // TEMPORARILY DISABLED to show FULL co-traveler tracks
      
      // Filter out co-traveler points that are IN Davis-Monthan
      // Coordinates are in GeoJSON format [lng, lat]
      const coTravelerPoints = ENABLE_DISPLAY_EXCLUSION ?
        coTravelerPointsRaw.filter(point => {
          const inDM = isPointInDavisMonthan(point.coordinates[0], point.coordinates[1]); // (lat, lon) - internal format
          return !inDM; // Keep points OUTSIDE Davis-Monthan
        }) :
        coTravelerPointsRaw; // Show ALL points when exclusion is disabled
      
      if (coTravelerPoints.length === 0) {
        return;
      }
      
      // Optimize: Use Map for O(1) duplicate detection instead of O(n²) filter
      const pointsMap = new Map<string, PathfinderDataPoint>();
      
      // Add existing points first
      dataPoints.forEach(point => pointsMap.set(point.id, point));
      
      // Add co-traveler points (will overwrite duplicates automatically)
      coTravelerPoints.forEach(point => {
        // Mark co-traveler points with special properties for visual distinction
        const enhancedPoint = {
          ...point,
          isCoTraveler: true, // Mark as co-traveler track
          coTravelerType: 'track', // Distinguish from encounter points
          originalStudentId: point.studentId, // Preserve original student ID
          // Add visual distinction properties
          trackType: 'co-traveler',
          trackOpacity: 0.7, // Slightly transparent
          trackColor: '#ff9800' // Orange color for co-traveler tracks
        };
        pointsMap.set(point.id, enhancedPoint);
      });
      
      const uniquePoints = Array.from(pointsMap.values());
      
      const coTravelerRoutesRaw = pathfinderApi.generateRoutes(coTravelerPoints);
      
      // Enhance co-traveler routes with special styling
      const coTravelerRoutes = coTravelerRoutesRaw.map(route => ({
        ...route,
        isCoTravelerRoute: true,
        routeType: 'co-traveler',
        routeColor: '#ff9800', // Orange for co-traveler routes
        routeOpacity: 0.7, // Slightly transparent
        routeWidth: 3, // Thinner than primary routes
        routeStyle: 'dashed' // Dashed line style
      }));
      
      const allRoutes = [...routes, ...coTravelerRoutes];
      
      // Optimize: Calculate time range incrementally instead of processing all points
      let newTimeRange = timeRange;
      if (coTravelerPoints.length > 0) {
        const coTravelerTimestamps = coTravelerPoints.map(p => p.timestamp.getTime());
        const coTravelerStart = Math.min(...coTravelerTimestamps);
        const coTravelerEnd = Math.max(...coTravelerTimestamps);
        
        newTimeRange = {
          start: new Date(Math.min(timeRange?.start?.getTime() || coTravelerStart, coTravelerStart)),
          end: new Date(Math.max(timeRange?.end?.getTime() || coTravelerEnd, coTravelerEnd))
        };
      }
      
      // Update data points first (most important for visualization)
      setDataPoints(uniquePoints);
      
      // CRITICAL: Update VISIBLE data points and routes to show co-travelers on map
      // Without this, the map only shows the original data, not the co-traveler tracks
      setVisibleDataPoints(uniquePoints);
      setVisibleRoutes(allRoutes);
      
      // Defer route and time range updates to next tick to prevent UI blocking
      setTimeout(() => {
        setRoutes(allRoutes);
        setTimeRange(newTimeRange);
      }, 0);
      
      
    } catch (error) {
      // Don't throw - this is supplementary data
    }
  }, [dataPoints, dateRange.start, dateRange.end, selectedCourse]);
  
  // Zoom map to fit all data points (including dwells)
  const zoomToFitData = (points: PathfinderDataPoint[], dwells: any[] = []) => {
    if (points.length === 0 && dwells.length === 0) return;
    
    // Internal coordinates are [latitude, longitude]
    // Calculate bounds from GPS points
    let minLat = points.length > 0 ? points[0].coordinates[0] : (dwells.length > 0 ? dwells[0].latitude : 0);
    let maxLat = points.length > 0 ? points[0].coordinates[0] : (dwells.length > 0 ? dwells[0].latitude : 0);
    let minLng = points.length > 0 ? points[0].coordinates[1] : (dwells.length > 0 ? dwells[0].longitude : 0);
    let maxLng = points.length > 0 ? points[0].coordinates[1] : (dwells.length > 0 ? dwells[0].longitude : 0);
    
    // Include GPS track points - coordinates are [lat, lng]
    points.forEach(point => {
      const lat = point.coordinates[0];
      const lng = point.coordinates[1];
      if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
      }
    });
    
    // Include dwell locations
    dwells.forEach(dwell => {
      if (dwell.latitude && dwell.longitude) {
        minLat = Math.min(minLat, dwell.latitude);
        maxLat = Math.max(maxLat, dwell.latitude);
        minLng = Math.min(minLng, dwell.longitude);
        maxLng = Math.max(maxLng, dwell.longitude);
      }
    });
    
    // Add padding (30% on each side for better context)
    const latPadding = (maxLat - minLat) * 0.3;
    const lngPadding = (maxLng - minLng) * 0.3;
    
    minLat -= latPadding;
    maxLat += latPadding;
    minLng -= lngPadding;
    maxLng += lngPadding;
    
    // Calculate center
    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;
    
    // Calculate zoom level based on bounds
    // Zoom out significantly to show all tracks with plenty of context
    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;
    const maxDiff = Math.max(latDiff, lngDiff);
    
    let zoom = 8; // Default - zoomed way out
    if (maxDiff < 0.01) zoom = 13; // Very small area
    else if (maxDiff < 0.05) zoom = 11; // Small area
    else if (maxDiff < 0.1) zoom = 10; // Medium-small area
    else if (maxDiff < 0.5) zoom = 8; // Medium area
    else if (maxDiff < 1) zoom = 7; // Large area
    else if (maxDiff < 5) zoom = 6; // Very large area
    else zoom = 5; // Huge area
    
    // Update map center/zoom and trigger extent update
    setMapCenter([centerLat, centerLng]);
    setMapZoom(zoom);
    setExtentUpdateTrigger(prev => prev + 1); // Trigger extent update
  };

  // Constant ID for the single uploaded data tab
  const UPLOADED_DATA_TAB_ID = 'uploaded-data';
  
  // Handle CSV data upload - DIRECT method without sessionStorage
  // All uploads go into a single "Uploaded Data" tab
  const handleCSVDataLoaded = useCallback((dataPoints: PathfinderDataPoint[], fileName: string) => {

    // Validate and prepare data points (coordinates are GeoJSON: [lng, lat])
    const validatedDataPoints = dataPoints.filter(point => {
      if (!point.coordinates || !Array.isArray(point.coordinates) || point.coordinates.length < 2) {
        return false;
      }
      const lng = parseFloat(String(point.coordinates[0]));
      const lat = parseFloat(String(point.coordinates[1]));
      if (isNaN(lat) || isNaN(lng)) {
        return false;
      }
      return true;
    });

    if (validatedDataPoints.length === 0) {
      alert('No valid data points found in CSV. Please check your file has valid coordinates.');
      return;
    }

    // Check if "Uploaded Data" tab already exists
    const existingUploadTab = analysisTabs.find(tab => tab.id === UPLOADED_DATA_TAB_ID);
    
    // Get existing uploaded data if tab exists
    const existingTabData = tabDataMap[UPLOADED_DATA_TAB_ID];
    const existingUploadedPoints = existingTabData?.dataPoints || [];
    const existingUploadedRoutes = existingTabData?.routes || [];
    
    // Merge new data with existing uploaded data
    const mergedUploadedPoints = [...existingUploadedPoints, ...validatedDataPoints];
    const newRoutes = pathfinderApi.generateRoutes(validatedDataPoints);
    const mergedUploadedRoutes = [...existingUploadedRoutes, ...newRoutes];

    // Store merged data in tabDataMap
    setTabDataMap(prev => ({
      ...prev,
      [UPLOADED_DATA_TAB_ID]: {
        dataPoints: mergedUploadedPoints,
        routes: mergedUploadedRoutes
      }
    }));

    // Get all tab data for map display
    const otherTabData = Object.entries(tabDataMap)
      .filter(([id]) => id !== UPLOADED_DATA_TAB_ID)
      .map(([, data]) => data);
    const allExistingPoints = otherTabData.flatMap(t => t.dataPoints);
    const allExistingRoutes = otherTabData.flatMap(t => t.routes);
    
    // Merge all data for map
    const mergedPoints = [...allExistingPoints, ...mergedUploadedPoints];
    const mergedRoutes = [...allExistingRoutes, ...mergedUploadedRoutes];

    // Update map data
    setDataPoints(mergedPoints);
    setRoutes(mergedRoutes);
    
    // ⚠️ CRITICAL: Also update TracerContext so Link Analysis page can access the data
    updateTracerState({ 
      dataPoints: mergedPoints,
      routes: mergedRoutes 
    });

    // Extract entity IDs and update selected students
    const entityIds = Array.from(new Set(validatedDataPoints.map(p => p.studentId || p.deviceId)));
    const updatedStudents = Array.from(new Set([...selectedStudents, ...entityIds]));
    setSelectedStudents(updatedStudents);

    // Update time range to include new data
    if (validatedDataPoints.length > 0) {
      const timestamps = validatedDataPoints.map((p: PathfinderDataPoint) => p.timestamp);
      const minTime = new Date(Math.min(...timestamps.map((t: Date) => t.getTime())));
      const maxTime = new Date(Math.max(...timestamps.map((t: Date) => t.getTime())));

      // Merge with existing time range if it exists
      const existingStart = timeRange?.start?.getTime() || minTime.getTime();
      const existingEnd = timeRange?.end?.getTime() || maxTime.getTime();

      const newTimeRange = {
        start: new Date(Math.min(existingStart, minTime.getTime())),
        end: new Date(Math.max(existingEnd, maxTime.getTime()))
      };

      setTimeRange(newTimeRange);
      setCurrentTime(maxTime); // Set current time to end to show all data
      
      // ⚠️ CRITICAL: Also update TracerContext timeRange for Link Analysis
      updateTracerState({ timeRange: newTimeRange });
    }

    // Convert all uploaded data to table format
    const csvDevices = mergedUploadedPoints.map((point: PathfinderDataPoint) => {
      const deviceData = { ...point.metadata };
      deviceData.id = point.id; // Include ID for deletion
      deviceData.registration_id = point.deviceId;
      deviceData.latitude = point.coordinates[0];
      deviceData.longitude = point.coordinates[1];
      deviceData.timestamp = point.timestamp.toISOString();
      deviceData.accuracy = point.accuracy || 0;
      deviceData.source = 'csv-upload';
      return deviceData;
    });

    // Extract column headers
    const csvColumns = csvDevices.length > 0 ? Object.keys(csvDevices[0]) : [];
    const uniqueDevices = new Set(mergedUploadedPoints.map(p => p.deviceId)).size;

    // Track uploaded files
    const existingFiles = existingUploadTab?.uploadedFiles || [];
    const cleanFileName = fileName.replace(/\.(csv|geojson|kml)$/i, '');
    const newFileList = [...existingFiles, cleanFileName];

    if (existingUploadTab) {
      // Update existing "Uploaded Data" tab
      setAnalysisTabs(prev => prev.map(tab => 
        tab.id === UPLOADED_DATA_TAB_ID
          ? {
              ...tab,
              data: csvDevices,
              totalDevices: uniqueDevices,
              timestamp: new Date().toISOString(),
              csvColumns: csvColumns,
              uploadedFiles: newFileList,
              polDataPoints: mergedUploadedPoints
            }
          : tab
      ));
    } else {
      // Create new "Uploaded Data" tab
      const newTab = {
        id: UPLOADED_DATA_TAB_ID,
        label: '📁 Uploads',
        data: csvDevices,
        totalDevices: uniqueDevices,
        timestamp: new Date().toISOString(),
        type: 'csv' as const,
        csvColumns: csvColumns,
        uploadedFiles: newFileList,
        polDataPoints: mergedUploadedPoints,
        alwaysVisible: true  // Flag to keep this data visible on map
      };
      addAnalysisTab(newTab);
    }

    // Show visualization - uploaded data is always visible by default
    setShowDataPoints(true);

    // Fit map to extent of loaded data
    setTimeout(() => {
      zoomToFitData(validatedDataPoints, []);
    }, 500);

  }, [tabDataMap, selectedStudents, timeRange, analysisTabs, addAnalysisTab, setAnalysisTabs, updateTracerState]);

  // Handle location search - creates a point at the searched location and adds to uploaded data
  const handleLocationSearch = useCallback((location: { lat: number; lng: number; name: string; source: 'address' | 'coordinates' | 'mgrs' }) => {
    const { lat, lng, name, source } = location;
    
    // Create a data point for this location
    const searchPoint: PathfinderDataPoint = {
      id: `search-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      deviceId: `📍 ${name.slice(0, 30)}${name.length > 30 ? '...' : ''}`,
      studentId: `search-point`,
      timestamp: new Date(),
      coordinates: [lat, lng] as [number, number], //  Internal format: [lat, lng]
      accuracy: 10,
      activity: `Searched ${source === 'address' ? 'address' : source === 'mgrs' ? 'MGRS' : 'coordinates'}`,
      metadata: {
        source: 'location-search',
        searchType: source,
        placeName: name,
        originalLat: lat,
        originalLng: lng
      }
    };

    // Get existing uploaded data
    const existingTabData = tabDataMap[UPLOADED_DATA_TAB_ID];
    const existingUploadedPoints = existingTabData?.dataPoints || [];
    const existingUploadedRoutes = existingTabData?.routes || [];
    
    // Merge new search point with existing uploaded data
    const mergedUploadedPoints = [...existingUploadedPoints, searchPoint];

    // Store merged data in tabDataMap
    setTabDataMap(prev => ({
      ...prev,
      [UPLOADED_DATA_TAB_ID]: {
        dataPoints: mergedUploadedPoints,
        routes: existingUploadedRoutes
      }
    }));

    // Get all tab data for map display
    const otherTabData = Object.entries(tabDataMap)
      .filter(([id]) => id !== UPLOADED_DATA_TAB_ID)
      .map(([, data]) => data);
    const allExistingPoints = otherTabData.flatMap(t => t.dataPoints);
    const allExistingRoutes = otherTabData.flatMap(t => t.routes);
    
    // Merge all data for map
    const mergedPoints = [...allExistingPoints, ...mergedUploadedPoints];
    const mergedRoutes = [...allExistingRoutes, ...existingUploadedRoutes];

    // Update map data
    setDataPoints(mergedPoints);
    setRoutes(mergedRoutes);
    
    // Update TracerContext
    updateTracerState({ 
      dataPoints: mergedPoints,
      routes: mergedRoutes 
    });

    // Convert to table format
    const searchDevice = {
      id: searchPoint.id, // Include ID for deletion
      registration_id: searchPoint.deviceId,
      latitude: lat,
      longitude: lng,
      timestamp: searchPoint.timestamp.toISOString(),
      accuracy: searchPoint.accuracy,
      source: 'search',
      place_name: name,
      search_type: source
    };

    // Check if "Uploaded Data" tab already exists
    const existingUploadTab = analysisTabs.find(tab => tab.id === UPLOADED_DATA_TAB_ID);
    
    // Get existing table data
    const existingTableData = existingUploadTab?.data || [];
    const updatedTableData = [...existingTableData, searchDevice];
    const uniqueDevices = new Set(mergedUploadedPoints.map(p => p.deviceId)).size;

    const shouldActivateUploadTab = !analysisTabs.some((tab: any) => tab.id === UPLOADED_DATA_TAB_ID);
    setAnalysisTabs(prev => {
      const existingUploadTab = prev.find((tab: any) => tab.id === UPLOADED_DATA_TAB_ID);
      if (existingUploadTab) {
        return prev.map((tab: any) =>
          tab.id === UPLOADED_DATA_TAB_ID
            ? { ...tab, data: updatedTableData, totalDevices: uniqueDevices,
                timestamp: new Date().toISOString(), polDataPoints: mergedUploadedPoints }
            : tab
        );
      } else {
        const newTab = {
          id: UPLOADED_DATA_TAB_ID,
          label: '📁 Uploads',
          data: updatedTableData,
          totalDevices: uniqueDevices,
          timestamp: new Date().toISOString(),
          type: 'csv' as const,
          csvColumns: Object.keys(searchDevice),
          uploadedFiles: [],
          polDataPoints: mergedUploadedPoints,
          alwaysVisible: true
        };
        return [...prev, newTab];
      }
    });
    if (shouldActivateUploadTab) {
      setActiveAnalysisTabId(UPLOADED_DATA_TAB_ID);
    }

    // Show visualization
    setShowDataPoints(true);

  }, [tabDataMap, analysisTabs, addAnalysisTab, setAnalysisTabs, updateTracerState]);

  // Handle deleting a single uploaded point
  const handleDeleteUploadedPoint = useCallback((pointId: string) => {
    // Get existing uploaded data
    const existingTabData = tabDataMap[UPLOADED_DATA_TAB_ID];
    if (!existingTabData) return;

    // Filter out the point to delete
    const updatedPoints = existingTabData.dataPoints.filter((p: PathfinderDataPoint) => {
      // Check various ID formats
      return p.id !== pointId && p.deviceId !== pointId;
    });

    // Update tabDataMap
    setTabDataMap(prev => ({
      ...prev,
      [UPLOADED_DATA_TAB_ID]: {
        dataPoints: updatedPoints,
        routes: existingTabData.routes
      }
    }));

    // Get all tab data for map display
    const otherTabData = Object.entries(tabDataMap)
      .filter(([id]) => id !== UPLOADED_DATA_TAB_ID)
      .map(([, data]) => data);
    const allExistingPoints = otherTabData.flatMap(t => t.dataPoints);
    const allExistingRoutes = otherTabData.flatMap(t => t.routes);
    
    // Merge all data for map
    const mergedPoints = [...allExistingPoints, ...updatedPoints];
    const mergedRoutes = [...allExistingRoutes, ...existingTabData.routes];

    // Update map data
    setDataPoints(mergedPoints);
    setRoutes(mergedRoutes);
    
    // Update TracerContext
    updateTracerState({ 
      dataPoints: mergedPoints,
      routes: mergedRoutes 
    });

    // Update analysis tab
    const existingUploadTab = analysisTabs.find(tab => tab.id === UPLOADED_DATA_TAB_ID);
    if (existingUploadTab) {
      const updatedTableData = (existingUploadTab.data || []).filter((d: any) => {
        return d.id !== pointId && d.registration_id !== pointId;
      });
      const uniqueDevices = new Set(updatedPoints.map((p: PathfinderDataPoint) => p.deviceId)).size;
      
      // If no more points, remove the tab entirely
      if (updatedPoints.length === 0) {
        removeAnalysisTab(UPLOADED_DATA_TAB_ID);
      } else {
        setAnalysisTabs(prev => prev.map(tab => 
          tab.id === UPLOADED_DATA_TAB_ID
            ? {
                ...tab,
                data: updatedTableData,
                totalDevices: uniqueDevices,
                polDataPoints: updatedPoints
              }
            : tab
        ));
      }
    }
  }, [tabDataMap, analysisTabs, setAnalysisTabs, updateTracerState, removeAnalysisTab]);

  // Handle clearing all uploaded data
  const handleClearAllUploadedData = useCallback(() => {
    // Clear uploaded data from tabDataMap
    setTabDataMap(prev => {
      const newMap = { ...prev };
      delete newMap[UPLOADED_DATA_TAB_ID];
      return newMap;
    });

    // Get all OTHER tab data for map display (excluding uploaded)
    const otherTabData = Object.entries(tabDataMap)
      .filter(([id]) => id !== UPLOADED_DATA_TAB_ID)
      .map(([, data]) => data);
    const allOtherPoints = otherTabData.flatMap(t => t.dataPoints);
    const allOtherRoutes = otherTabData.flatMap(t => t.routes);

    // Update map data with only other tabs' data
    setDataPoints(allOtherPoints);
    setRoutes(allOtherRoutes);
    
    // Update TracerContext
    updateTracerState({ 
      dataPoints: allOtherPoints,
      routes: allOtherRoutes 
    });

    // Remove the uploaded data tab
    removeAnalysisTab(UPLOADED_DATA_TAB_ID);
  }, [tabDataMap, updateTracerState, removeAnalysisTab]);

  const handleLoadFiles = useCallback((
    files: { dataPoints: PathfinderDataPoint[]; fileName: string }[],
    mode: 'as-one' | 'separate'
  ) => {
    if (mode === 'as-one') {
      const allPoints = files.flatMap(f => f.dataPoints);
      const combinedFileName = files.map(f => f.fileName).join(', ');
      handleCSVDataLoaded(allPoints, combinedFileName);
    } else {
      const newTabs: any[] = [];
      const newTabDataEntries: Record<string, { dataPoints: PathfinderDataPoint[]; routes: PathfinderRoute[] }> = {};

      for (const file of files) {
        const validatedDataPoints = file.dataPoints.filter(point => {
          if (!point.coordinates || !Array.isArray(point.coordinates) || point.coordinates.length < 2) return false;
          const lng = parseFloat(String(point.coordinates[0]));
          const lat = parseFloat(String(point.coordinates[1]));
          return !isNaN(lat) && !isNaN(lng);
        });
        if (validatedDataPoints.length === 0) continue;

        const newRoutes = pathfinderApi.generateRoutes(validatedDataPoints);
        const cleanFileName = file.fileName.replace(/\.(csv|geojson|kml)$/i, '');
        const tabId = `uploaded-${cleanFileName}-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;

        newTabDataEntries[tabId] = {
          dataPoints: validatedDataPoints,
          routes: newRoutes
        };

        const csvDevices = validatedDataPoints.map((point: PathfinderDataPoint) => {
          const deviceData = { ...point.metadata };
          deviceData.id = point.id;
          deviceData.registration_id = point.deviceId;
          deviceData.latitude = point.coordinates[0];
          deviceData.longitude = point.coordinates[1];
          deviceData.timestamp = point.timestamp.toISOString();
          deviceData.accuracy = point.accuracy || 0;
          deviceData.source = 'csv-upload';
          return deviceData;
        });

        const csvColumns = csvDevices.length > 0 ? Object.keys(csvDevices[0]) : [];
        const uniqueDevices = new Set(validatedDataPoints.map(p => p.deviceId)).size;

        newTabs.push({
          id: tabId,
          label: cleanFileName,
          data: csvDevices,
          totalDevices: uniqueDevices,
          timestamp: new Date().toISOString(),
          type: 'csv' as const,
          csvColumns: csvColumns,
          uploadedFiles: [file.fileName],
          polDataPoints: validatedDataPoints,
          alwaysVisible: false
        });
      }

      if (newTabs.length > 0) {
        // Add all tabs at once
        setTabDataMap(prev => ({ ...prev, ...newTabDataEntries }));
        setAnalysisTabs((prev: any[]) => [...prev, ...newTabs]);
        setActiveAnalysisTabId(newTabs[newTabs.length - 1].id);
        setShowDataPoints(true);

        const allNewPoints = files.flatMap(f => f.dataPoints);
        setTimeout(() => {
          zoomToFitData(allNewPoints, []);
        }, 500);
      }
    }
  }, [handleCSVDataLoaded, zoomToFitData]);


    // Handle CSV upload from sessionStorage (both on mount and via event)
  const processCsvFromStorage = useCallback(() => {
    const csvData = sessionStorage.getItem('csvUploadData');
    if (!csvData) return;

    try {
      const parsed = JSON.parse(csvData);
      const uploadTime = new Date();

      const processPoints = (dataPoints: any[]) => {
        return dataPoints.map((point: any) => {
          let timestamp = uploadTime;
          if (point.timestamp) {
            try {
              timestamp = new Date(point.timestamp);
              if (isNaN(timestamp.getTime())) timestamp = uploadTime;
            } catch { timestamp = uploadTime; }
          }
          const lng = parseFloat(String(point.coordinates[0]));
          const lat = parseFloat(String(point.coordinates[1]));
          return {
            ...point,
            timestamp,
            coordinates: [lng, lat] as [number, number]
          };
        });
      };

      if (parsed.files && parsed.mode) {
        const processedFiles = parsed.files.map((f: any) => ({
          dataPoints: processPoints(f.dataPoints),
          fileName: f.fileName,
        }));
        handleLoadFiles(processedFiles, parsed.mode);
      }
      sessionStorage.removeItem('csvUploadData');
    } catch (error) {
      sessionStorage.removeItem('csvUploadData');
    }
  }, [handleLoadFiles]);

  // Check for CSV upload on mount
  useEffect(() => {
    processCsvFromStorage();
  }, [processCsvFromStorage]);

  // Listen for CSV data available event (when already on page)
  useEffect(() => {
    const handleCsvEvent = () => {
      processCsvFromStorage();
    };

    window.addEventListener('csv-data-available', handleCsvEvent);
    return () => {
      window.removeEventListener('csv-data-available', handleCsvEvent);
    };
  }, [processCsvFromStorage]);

  // Only clear data when students or date range changes, don't auto-load
  // BUT: Don't clear if we have analysis tabs (Area Search / PoL workflow)
  useEffect(() => {
    // Skip clearing if we have tabs - tab workflow manages its own data
    if (analysisTabs.length > 0) {
      return;
    }
    
    if (selectedStudents.length === 0 || !dateRange.start || !dateRange.end) {
      // Clear data when no students selected or no date range
      setDataPoints([]);
      setVisibleDataPoints([]);
      setRoutes([]);
      setVisibleRoutes([]);
      setTimeRange(null);
      setActualDevices({});
      setSelectedDevices({});
    }
    // Don't auto-load data - user must click the Load button
  }, [selectedStudents, dateRange, analysisTabs.length]);

  // Handle zoom to specific location (used by PoL Analysis clicks)
  const handleZoomToLocation = useCallback((lat: number, lng: number, zoom: number = 16) => {
    // Save original position before zooming (only if not already saved)
    // This allows us to "zoom back" when the user deselects
    if (!originalMapViewRef.current) {
      originalMapViewRef.current = { center: mapCenter, zoom: mapZoom };
    }
    
    // Skip onMapChange updates during the flyTo animation
    skipMapChangeRef.current = true;
    setMapCenter([lat, lng]);
    setMapZoom(zoom);
    // Set highlight location to trigger the pulse animation
    setHighlightLocation({ lat, lng });
    // Re-enable onMapChange after animation completes
    setTimeout(() => {
      skipMapChangeRef.current = false;
      setHighlightLocation(null);
    }, 2000);
  }, [mapCenter, mapZoom]);

  // Handle loading Overwatch articles to map
  const handleLoadArticlesToMap = useCallback((articles: OverwatchArticle[]) => {
    setOverwatchArticles(articles);
    // If articles have locations, optionally zoom to fit them
    const articlesWithLocation = articles.filter(a => a.latitude && a.longitude);
    if (articlesWithLocation.length > 0) {
      console.log(`[Overwatch] Loaded ${articlesWithLocation.length} articles to map`);
    }
  }, []);

  // Handle clearing Overwatch articles from map
  const handleClearOverwatchArticles = useCallback(() => {
    setOverwatchArticles([]);
    console.log('[Overwatch] Cleared articles from map');
  }, []);

  // Handle clicking an Overwatch article on map
  const handleOverwatchArticleClick = useCallback((article: OverwatchArticle) => {
    if (article.latitude && article.longitude) {
      handleZoomToLocation(article.latitude, article.longitude, 14);
    }
    // Could also open a popup or panel with article details
    console.log('[Overwatch] Article clicked:', article.title);
  }, [handleZoomToLocation]);
  
  // Handle reset map view (return to original position before zooming)
  const handleResetMapView = useCallback(() => {
    if (originalMapViewRef.current) {
      skipMapChangeRef.current = true;
      setMapCenter(originalMapViewRef.current.center);
      setMapZoom(originalMapViewRef.current.zoom);
      originalMapViewRef.current = null; // Clear saved position
      setTimeout(() => {
        skipMapChangeRef.current = false;
      }, 1000);
    }
  }, []);

  // Handle zoom to device extent (show all points for a device)
  const handleZoomToDeviceExtent = useCallback((_: string, points: any[]) => {
    if (!points || points.length === 0) return;
    
    // Filter to only points with valid coordinates
    // Support both {latitude, longitude} and {coordinates: [lat, lng]} formats
    const validPoints = points.filter(point => {
      const lat = point.latitude ?? point.coordinates?.[0];
      const lng = point.longitude ?? point.coordinates?.[1];
      return lat !== undefined && lng !== undefined && 
             !isNaN(lat) && !isNaN(lng) &&
             lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
    });
    
    if (validPoints.length === 0) return;
    
    // Calculate bounds
    let minLat = Infinity, maxLat = -Infinity;
    let minLng = Infinity, maxLng = -Infinity;
    
    validPoints.forEach(point => {
      const lat = point.latitude ?? point.coordinates?.[0];
      const lng = point.longitude ?? point.coordinates?.[1];
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
    });
    
    // Add padding to bounds (10% on each side)
    const latPadding = (maxLat - minLat) * 0.15 || 0.01; // Use 0.01 if single point
    const lngPadding = (maxLng - minLng) * 0.15 || 0.01;
    
    minLat -= latPadding;
    maxLat += latPadding;
    minLng -= lngPadding;
    maxLng += lngPadding;
    
    // Calculate center
    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;
    
    // Calculate appropriate zoom level based on extent
    // Use the larger dimension to ensure all points fit
    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;
    
    // Account for latitude distortion (longitude degrees are smaller near poles)
    const latRad = centerLat * Math.PI / 180;
    const adjustedLngDiff = lngDiff * Math.cos(latRad);
    
    const maxDiff = Math.max(latDiff, adjustedLngDiff);
    
    // More conservative zoom calculation to ensure all points are visible
    // Based on roughly 256px tiles at zoom 0 = 360 degrees
    let zoom: number;
    if (maxDiff > 10) zoom = 4;
    else if (maxDiff > 5) zoom = 5;
    else if (maxDiff > 2) zoom = 6;
    else if (maxDiff > 1) zoom = 7;
    else if (maxDiff > 0.5) zoom = 8;
    else if (maxDiff > 0.25) zoom = 9;
    else if (maxDiff > 0.1) zoom = 10;
    else if (maxDiff > 0.05) zoom = 11;
    else if (maxDiff > 0.025) zoom = 12;
    else if (maxDiff > 0.01) zoom = 13;
    else if (maxDiff > 0.005) zoom = 14;
    else if (maxDiff > 0.002) zoom = 15;
    else zoom = 14; // Default for very small areas - don't zoom in too much
    
    setMapCenter([centerLat, centerLng]);
    setMapZoom(zoom);
  }, []);

  // Story Mode callbacks (memoized to prevent infinite loops)
  const handleStoryModeMapChange = useCallback((center: [number, number]) => {
    setMapCenter(center);
    setMapZoom(15); // Zoom in to show the location clearly
  }, []);

  const handleStoryModeTimeChange = useCallback((time: Date) => {
            setCurrentTime(time);
  }, []);

  // Time slider callbacks
  const handleTimeUpdate = useCallback((time: Date, points: PathfinderDataPoint[], progressiveRoutes: any[]) => {
    
    // Apply metadata filters to time-filtered points
    const filteredPoints = applyMetadataFilters(points);
    
    // Filter dwells that overlap with the visible time window
    // Calculate window based on visible points
    let windowStart = time;
    let windowEnd = time;
    
    if (filteredPoints.length > 0) {
      // Use the actual time range of visible points
      const pointTimes = filteredPoints.map(p => p.timestamp.getTime());
      windowStart = new Date(Math.min(...pointTimes));
      windowEnd = new Date(Math.max(...pointTimes));
    } else {
      // If no points, use a small window around current time
      windowStart = new Date(time.getTime() - 60000); // 1 minute before
      windowEnd = new Date(time.getTime() + 60000); // 1 minute after
    }
    
    // Collect all selected device IDs for filtering
    const selectedDeviceIds = Object.values(selectedDevices).flat();
    
    // Safety check: ensure dwellLocations is an array
    const dwellsArray = Array.isArray(dwellLocations) ? dwellLocations : [];
    
    // Filter dwells that overlap with the time window AND match selected devices AND not hidden
    const visibleDwellsInWindow = dwellsArray.filter(dwell => {
      const dwellStart = new Date(dwell.start_time).getTime();
      const dwellEnd = new Date(dwell.end_time).getTime();
      const winStart = windowStart.getTime();
      const winEnd = windowEnd.getTime();
      
      // Check time overlap: dwell starts before window ends AND dwell ends after window starts
      const timeOverlap = dwellStart <= winEnd && dwellEnd >= winStart;
      if (!timeOverlap) return false;
      
      // Check if the dwell's device is hidden
      const entityId = dwell.entity_id || dwell.entityId || '';
      const deviceId = dwell.device_id || dwell.deviceId || '';
      const isHidden = hiddenDevices.has(entityId) || 
                       hiddenDevices.has(deviceId) ||
                       hiddenDevices.has(`GN-${entityId}`) ||
                       hiddenDevices.has(`GN-${deviceId}`) ||
                       hiddenDevices.has(entityId.replace('GN-', '')) ||
                       hiddenDevices.has(deviceId.replace('GN-', ''));
      if (isHidden) return false;
      
      // If no devices selected, show all dwells (default behavior)
      if (selectedDeviceIds.length === 0) return true;
      
      // Filter by device: check if dwell's device matches any selected device
      // Dwell has entityId and deviceType, construct deviceKey like "Student-XX-devicetype"
      const dwellDeviceKey = `${dwell.entityId || dwell.entity_id}-${(dwell.deviceType || dwell.device_type || 'phone').toLowerCase()}`;
      return selectedDeviceIds.includes(dwellDeviceKey);
    });
    
    
    // React 18+ automatic batching - all updates in same event are batched automatically
    setCurrentTime(time);
    setVisibleDataPoints(filteredPoints); // Use filtered points
    setVisibleRoutes(progressiveRoutes);
    setVisibleDwells(visibleDwellsInWindow);
    
    // If follow mode is enabled, center map on the point closest to current playback time
    if (followMode && points.length > 0) {
      // Find the point closest to the current playback time (not just the last in array)
      const currentTimeMs = time.getTime();
      let closestPoint = points[0];
      let minTimeDiff = Math.abs(points[0].timestamp.getTime() - currentTimeMs);
      
      for (const point of points) {
        const timeDiff = Math.abs(point.timestamp.getTime() - currentTimeMs);
        if (timeDiff < minTimeDiff) {
          minTimeDiff = timeDiff;
          closestPoint = point;
        }
      }
      
      const newCenter: [number, number] = [closestPoint.coordinates[0], closestPoint.coordinates[1]];
      setMapCenter(newCenter);
    }
  }, [followMode, dwellLocations, selectedDevices, applyMetadataFilters]);

  const handleFollowModeChange = useCallback((enabled: boolean) => {
    setFollowMode(enabled);
  }, []);

  // Create studentColors mapping for MapGL component - each device gets unique color
  // Uses shared generateDeviceColor function from theme/entityColors
  const studentColors = useMemo(() => {
    const colors: Record<string, string> = {};
    const seenDevices = new Set<string>();
    
    // Assign unique colors to each device from data points
    visibleDataPoints.forEach(point => {
      const deviceId = point.deviceId || point.studentId || '';
      const studentId = point.studentId || point.deviceId || '';
      
      // Generate unique color for this device if not already assigned
      if (deviceId && !seenDevices.has(deviceId)) {
        seenDevices.add(deviceId);
        const color = generateDeviceColor(deviceId);
        colors[deviceId] = color;
      
        // Also map by studentId directly (same as deviceId for most cases)
        if (studentId && studentId !== deviceId) {
          colors[studentId] = color;
        }
        
        // Also map by student-deviceType combination for route lookups
        const deviceType = (point.deviceType || 'phone').toLowerCase();
        if (studentId && deviceType) {
          colors[`${studentId}-${deviceType}`] = color;
        }
      }
    });
    
    // Also color routes with unique colors per device - ensure route.studentId has color
    visibleRoutes.forEach(route => {
      // Use first point's deviceId to get consistent color
      const pointDeviceId = route.points?.[0]?.deviceId || route.studentId || route.deviceId;
      
      if (pointDeviceId && !colors[pointDeviceId]) {
        const color = generateDeviceColor(pointDeviceId);
        colors[pointDeviceId] = color;
      }
      
      // Ensure route.studentId has the same color as its points
      if (route.studentId && !colors[route.studentId]) {
        const color = colors[pointDeviceId] || generateDeviceColor(route.studentId);
        colors[route.studentId] = color;
      }
      
      // Ensure route.deviceId also maps to the same color
      if (route.deviceId && !colors[route.deviceId]) {
        const color = colors[pointDeviceId] || colors[route.studentId] || generateDeviceColor(route.deviceId);
        colors[route.deviceId] = color;
      }
    });
    
    return colors;
  }, [visibleDataPoints, visibleRoutes]);

  // Handle loading Pattern of Life for a specific device
  const handleLoadDeviceData = useCallback(async (registrationId: string) => {
    
    // Show loading notification
    const shortId = registrationId.slice(0, 8);
    setPolLoading(true);
    setPolLoadingMessage(`Loading Pattern of Life for ${shortId}...`);
    
    // Update selected students to focus on this device
    setSelectedStudents([registrationId]);
    
    // Find the tab that contains this device to get the time range
    const sourceTab = analysisTabs.find(tab => 
      tab.data.some(d => d.registration_id === registrationId)
    );
    
    if (!sourceTab) {
      setPolLoading(false);
      alert('Could not find source data for this device');
      return;
    }
    
    try {
      // Use the same time range as the original search
      const startDate = dateRange.start || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      const endDate = dateRange.end || new Date();
      
      // NOTE: Do NOT pass geographic filter for PoL - we want ALL device data
      // The device was discovered in the area, but their full pattern of life
      // may extend far beyond the search radius
      const patternData = await pathfinderApi.getGeoNeutronPatternOfLife({
        registrationIds: [registrationId],
        startDate: startDate.toISOString().replace(/\.\d{3}Z$/, 'Z'),
        endDate: endDate.toISOString().replace(/\.\d{3}Z$/, 'Z')
        // No areaCenter/areaRadius - get full PoL data
      });
      
      // devices is a Dict[str, List] - check if it has any keys
      const deviceIds = patternData?.devices ? Object.keys(patternData.devices) : [];
      if (patternData && deviceIds.length > 0) {
        // Transform to PathfinderDataPoint format - iterate over device dict
        const newPoints: PathfinderDataPoint[] = [];
        for (const [devId, locations] of Object.entries(patternData.devices)) {
          const deviceLocations = locations as any[];
          deviceLocations.forEach((point: any, index: number) => {
            let coordinates: [number, number] = [0, 0];
            // Handle coordinates - internal format is [latitude, longitude]
            if (point.latitude !== undefined && point.longitude !== undefined) {
              coordinates = [parseFloat(point.latitude), parseFloat(point.longitude)]; // Internal: [lat, lng]
            } else if (point.coordinates && Array.isArray(point.coordinates) && point.coordinates.length >= 2) {
              // Assume coordinates are already in internal [lat, lng] format
              coordinates = [parseFloat(point.coordinates[0]), parseFloat(point.coordinates[1])];
            }
            
            newPoints.push({
              id: point.id || `pol-${devId}-${index}`,
              studentId: devId, // Use device ID as studentId
              deviceId: devId,
              deviceType: 'phone',
              timestamp: new Date(point.timestamp),
              coordinates: coordinates,
              accuracy: point.accuracy || 0,
              activity: 'movement',
              metadata: {
                registration_id: devId,
                speed: point.speed,
                bearing: point.bearing,
                source: 'pattern_of_life',
                ip_address: point.ip_address,
                forensic_flag: point.forensic_flag,
                country: point.country,
                carrier: point.carrier,
                timezone: point.timezone
              }
            });
          });
        }

        // Filter out invalid points (0,0 coordinates)
        const validPoints = newPoints.filter(p => p.coordinates[0] !== 0 || p.coordinates[1] !== 0);
        
        if (validPoints.length > 0) {
          // Generate routes for this device
          const tabRoutes = pathfinderApi.generateRoutes(validPoints);
          
          // Create a new tab for this device's PoL
          const tabId = `pol-${Date.now()}`;
          const shortId = registrationId.slice(0, 8);
          const tabLabel = `PoL: ${shortId}`;
          
          // Store tab data in map
          setTabDataMap(prev => ({
            ...prev,
            [tabId]: {
              dataPoints: validPoints,
              routes: tabRoutes
            }
          }));
          
          // Update local state immediately
          setDataPoints(validPoints);
          setVisibleDataPoints(validPoints);
          setRoutes(tabRoutes);
          setVisibleRoutes(tabRoutes);
          
          // Detect dwells client-side
          const detectedDwells = detectDwells(validPoints);
          const { dwells: processedDwells, sites: newSites } = identifySites(detectedDwells, 150, validPoints);
          
          // Update dwell state
          setDwellLocations(processedDwells);
          setVisibleDwells(processedDwells);
          setDwellSites(newSites);
          
          // ⚠️ Sync to TracerContext for Link Analysis
          updateTracerState({ dwellLocations: processedDwells });
          
          // Convert data points to the format expected by the table
          const polDevices = validPoints.map((point: PathfinderDataPoint) => ({
            registration_id: point.deviceId,
            latitude: point.coordinates[0],  // Internal format: [lat, lng]
            longitude: point.coordinates[1],
            timestamp: point.timestamp.toISOString(),
            accuracy: point.accuracy || 0,
            source: 'geoneutron-pol',
            ...point.metadata
          }));

          // Fetch device details (Explore / device bulk API) for extra fields, same as area search
          let exploreDeviceDetails: Record<string, any> = {};
          try {
            const bulkRes = await geoneutronApi.getDevicesBulk([registrationId]);
            const devices = bulkRes?.devices ?? [];
            devices.forEach((d: any) => {
              const id = (d.registrationID ?? d.registration_id ?? '').replace(/^GN-/i, '');
              if (id) exploreDeviceDetails[id] = d;
            });
          } catch {
            // Non-blocking
          }

          const tabData = polDevices.map((d: any) => {
            const regId = (d.registration_id || '').replace(/^GN-/i, '');
            const explore = exploreDeviceDetails?.[regId];
            if (!explore) return d;
            return {
              ...d,
              browser_family: explore.userAgentBrowserFamily ?? null,
              browser_version: explore.userAgentBrowserVersion ?? null,
              os_family: explore.userAgentOSFamily ?? null,
              os_version: normalizeOsVersion(explore.userAgentOSVersion),
              device_family: explore.userAgentDeviceFamily ?? null,
              device_brand: explore.userAgentDeviceBrand ?? null,
              device_model: explore.userAgentDeviceModel ?? null,
              advertiser_eu: explore.eu ?? null,
              user_agent_full: explore.userAgentFull ?? null,
              created_on: explore.createdOn ?? null,
              updated_on: explore.updatedOn ?? null,
            };
          });
          
          // Create the new tab
          const newTab = {
            id: tabId,
            label: tabLabel,
            data: tabData,
            totalDevices: 1,
            timestamp: new Date().toISOString(),
            circleInfo: sourceTab?.circleInfo,
            type: 'pol' as const,
            deviceId: registrationId,
            polDataPoints: validPoints,
            dwellLocations: processedDwells,
            exploreDeviceDetails: Object.keys(exploreDeviceDetails).length > 0 ? exploreDeviceDetails : undefined,
          };
          
          // Add the tab (this will also set it as active)
          addAnalysisTab(newTab);
          
          // Update time range
          const timestamps = validPoints.map((p: PathfinderDataPoint) => p.timestamp);
          const minTime = new Date(Math.min(...timestamps.map((t: Date) => t.getTime())));
          const maxTime = new Date(Math.max(...timestamps.map((t: Date) => t.getTime())));
          setTimeRange({ start: minTime, end: maxTime });
          setCurrentTime(maxTime);
          
          // Zoom to the new data
          zoomToFitData(validPoints);
          
          setPolLoadingMessage(`✅ Loaded ${validPoints.length} points for ${shortId}`);
          // Auto-close success message after 3 seconds
          setTimeout(() => setPolLoading(false), 3000);
        } else {
          setPolLoading(false);
          alert('No valid location points found for this device (all coordinates invalid).');
        }
      } else {
        setPolLoading(false);
        alert('⚠️ No Pattern of Life data found for this device in the selected date range.');
      }
    } catch (error: any) {
      console.error('Pattern of Life load error:', error);
      setPolLoading(false);
      if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
        alert(`⏱️ Request timed out.\n\nThe Pattern of Life search took too long.\n\nTry reducing the date range.`);
      } else {
        alert(`Failed to load device data: ${error?.message || 'Check console for details.'}`);
      }
    }
  }, [analysisTabs, dateRange]);

  // Handle loading Pattern of Life for ALL devices from an area search
  const handleLoadAllDevicesData = useCallback(async (registrationIds: string[]) => {
    if (registrationIds.length === 0) {
      alert('No devices to load');
      return;
    }
    
    // Confirm with user if loading many devices
    if (registrationIds.length > 10) {
      const confirmed = window.confirm(
        `You are about to load Pattern of Life data for ${registrationIds.length} devices. This may take a while. Continue?`
      );
      if (!confirmed) return;
    }
    
    setPolLoading(true);
    
    try {
      // Use global date range filter, with 90-day fallback
      let startDate = dateRange.start;
      let endDate = dateRange.end;
      
      // If no date range selected, use last 90 days (max allowed by API)
      if (!startDate || !endDate) {
        endDate = new Date();
        startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
      }
      
      // PERFORMANCE: Load devices in chunks to prevent UI freeze
      const CHUNK_SIZE = 5;
      const chunks: string[][] = [];
      for (let i = 0; i < registrationIds.length; i += CHUNK_SIZE) {
        chunks.push(registrationIds.slice(i, i + CHUNK_SIZE));
      }
      
      const allDeviceData: Record<string, any[]> = {};
      
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        const chunk = chunks[chunkIndex];
        setPolLoadingMessage(`Loading PoL: chunk ${chunkIndex + 1}/${chunks.length} (${chunk.length} devices)...`);
        
        // Yield to UI thread between chunks
        await new Promise(resolve => setTimeout(resolve, 0));
        
        const patternData = await pathfinderApi.getGeoNeutronPatternOfLife({
          registrationIds: chunk,
          startDate: startDate.toISOString().replace(/\.\d{3}Z$/, 'Z'),
          endDate: endDate.toISOString().replace(/\.\d{3}Z$/, 'Z')
        });
        
        if (patternData?.devices) {
          Object.assign(allDeviceData, patternData.devices);
        }
      }
      
      // Wrap in same structure as original API response
      const patternData = { devices: allDeviceData };
      const deviceIds = patternData?.devices ? Object.keys(patternData.devices) : [];
      
      if (patternData && deviceIds.length > 0) {
        // Transform to PathfinderDataPoint format
        const newPoints: PathfinderDataPoint[] = [];
        for (const [devId, locations] of Object.entries(patternData.devices)) {
          const deviceLocations = locations as any[];
          deviceLocations.forEach((point: any, index: number) => {
            // API returns separate lat/lon fields - convert to GeoJSON [lng, lat] format
            let lng = 0, lat = 0;
            if (point.latitude !== undefined && point.longitude !== undefined) {
              lat = parseFloat(point.latitude);
              lng = parseFloat(point.longitude);
            } else if (point.coordinates && Array.isArray(point.coordinates) && point.coordinates.length >= 2) {
              // Coordinates array could be [lat, lng] or [lng, lat] - need to detect
              const coord0 = parseFloat(point.coordinates[0]);
              const coord1 = parseFloat(point.coordinates[1]);
              // If coord0 is in longitude range (-180 to 180) but NOT in latitude range (-90 to 90), it's [lng, lat]
              // Otherwise assume [lat, lng] which is common in many APIs
              if (Math.abs(coord0) > 90 && Math.abs(coord1) <= 90) {
                lng = coord0;
                lat = coord1;
              } else {
                // Assume [lat, lng] format (common convention)
                lat = coord0;
                lng = coord1;
              }
            }
            
            // Validate coordinates - skip invalid points
            if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
              return;
            }
            
            newPoints.push({
              id: point.id || `pol-all-${devId}-${index}`,
              studentId: devId,
              deviceId: devId,
              deviceType: 'phone',
              timestamp: new Date(point.timestamp),
              coordinates: [lat, lng] as [number, number], // Internal format [lat, lng]
              accuracy: point.accuracy || 0,
              activity: 'movement',
              metadata: {
                registration_id: devId,
                speed: point.speed,
                bearing: point.bearing,
                source: 'pattern_of_life',
                ip_address: point.ip_address,
                forensic_flag: point.forensic_flag,
                country: point.country,
                carrier: point.carrier,
                timezone: point.timezone
              }
            });
          });
        }

        // Filter out points at [0, 0] (null island)
        let validPoints = newPoints.filter(p => !(p.coordinates[0] === 0 && p.coordinates[1] === 0));
        
        // PERFORMANCE: Limit max points to prevent UI freeze (sample evenly if too many)
        const MAX_POINTS = 10000;
        if (validPoints.length > MAX_POINTS) {
          const sampleRate = MAX_POINTS / validPoints.length;
          validPoints = validPoints.filter((_, __) => Math.random() < sampleRate).slice(0, MAX_POINTS);
          setPolLoadingMessage(`Sampled ${validPoints.length} of ${newPoints.length} points for performance...`);
          await new Promise(resolve => setTimeout(resolve, 100)); // Let user see message
        }
        
        if (validPoints.length > 0) {
          setPolLoadingMessage(`Processing ${validPoints.length} points...`);
          await new Promise(resolve => setTimeout(resolve, 0)); // Yield to UI
          
          // Generate routes for all devices
          const tabRoutes = pathfinderApi.generateRoutes(validPoints);
          
          // Create a new tab for all PoL data
          const tabId = `pol-all-${Date.now()}`;
          const tabLabel = `PoL: ${deviceIds.length} devices`;
          
          // Store tab data
          setTabDataMap(prev => ({
            ...prev,
            [tabId]: {
              dataPoints: validPoints,
              routes: tabRoutes
            }
          }));
          
          // Update local state
          setDataPoints(validPoints);
          setVisibleDataPoints(validPoints);
          setRoutes(tabRoutes);
          setVisibleRoutes(tabRoutes);
          
          // Detect dwells (yield to UI first for large datasets)
          setPolLoadingMessage(`Detecting dwells in ${validPoints.length} points...`);
          await new Promise(resolve => setTimeout(resolve, 0));
          const detectedDwells = detectDwells(validPoints);
          
          setPolLoadingMessage(`Identifying sites from ${detectedDwells.length} dwells...`);
          await new Promise(resolve => setTimeout(resolve, 0));
          const { dwells: processedDwells, sites: newSites } = identifySites(detectedDwells, 150, validPoints);
          setDwellLocations(processedDwells);
          setVisibleDwells(processedDwells);
          setDwellSites(newSites);
          
          // ⚠️ Sync to TracerContext for Link Analysis
          updateTracerState({ dwellLocations: processedDwells });
          
          // Convert to table format
          const polDevices = validPoints.map((point: PathfinderDataPoint) => ({
            registration_id: point.deviceId,
            latitude: point.coordinates[0],  // Internal format: [lat, lng]
            longitude: point.coordinates[1],
            timestamp: point.timestamp.toISOString(),
            accuracy: point.accuracy || 0,
            source: 'geoneutron-pol',
            ...point.metadata
          }));
          
          // Create the new tab
          const newTab = {
            id: tabId,
            label: tabLabel,
            data: polDevices,
            totalDevices: deviceIds.length,
            timestamp: new Date().toISOString(),
            type: 'pol' as const,
            polDataPoints: validPoints,
            dwellLocations: processedDwells
          };
          
          addAnalysisTab(newTab);
          
          // Update time range
          const timestamps = validPoints.map((p: PathfinderDataPoint) => p.timestamp);
          const minTime = new Date(Math.min(...timestamps.map((t: Date) => t.getTime())));
          const maxTime = new Date(Math.max(...timestamps.map((t: Date) => t.getTime())));
          setTimeRange({ start: minTime, end: maxTime });
          setCurrentTime(maxTime);
          
          // Zoom to fit
          zoomToFitData(validPoints);
          
          setPolLoadingMessage(`✅ Loaded ${validPoints.length} points for ${deviceIds.length} devices`);
          setTimeout(() => setPolLoading(false), 3000);
        } else {
          setPolLoading(false);
          alert('No valid location points found for the selected devices.');
        }
      } else {
        setPolLoading(false);
        alert('⚠️ No Pattern of Life data found for the selected devices in the current date range.');
      }
    } catch (error: any) {
      console.error('Failed to load all device data:', error);
      setPolLoading(false);
      if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
        alert(`⏱️ Request timed out.\n\nThe Pattern of Life search took too long.\n\nTry:\n- Reducing the date range\n- Loading fewer devices at once`);
      } else {
        alert(`Failed to load device data: ${error?.message || 'Check console for details.'}`);
      }
    }
  }, [dateRange, addAnalysisTab, zoomToFitData]);

  // Track which devices are hidden on the map
  const [hiddenDevices, setHiddenDevices] = useState<Set<string>>(new Set());
  
  // Chart-based time filtering (from Interactive Charts)
  const [chartFilter, setChartFilter] = useState<ChartFilter | null>(null);
  
  // Track which devices are saved/favorited in watchlist
  const [savedDeviceIds, setSavedDeviceIds] = useState<Set<string>>(new Set());
  
  // Load saved device IDs from watchlist
  useEffect(() => {
    const loadSavedDevices = async () => {
  try {
    const watchlist = await pathfinderApi.getWatchlist();
    const savedIds = new Set<string>();
    const nameMap = new Map<string, string>();

    for (const d of watchlist) {
      const rid = d.registration_id;
      const name = d.name?.trim();
      savedIds.add(rid);
      savedIds.add(`GN-${rid}`);
      if (name) {
        nameMap.set(rid, name);
        nameMap.set(`GN-${rid}`, name);
      }
    }

    setSavedDeviceIds(savedIds);
    setSavedDeviceNames(nameMap);
  } catch (error) {
  }
};
    loadSavedDevices();
    
    // Listen for watchlist refresh events
    const handleRefresh = () => loadSavedDevices();
    window.addEventListener('refresh-watchlist', handleRefresh);
    return () => window.removeEventListener('refresh-watchlist', handleRefresh);
  }, []);
  
  // Measurement system state
  const [measurementSystem, setMeasurementSystem] = useState<'metric' | 'imperial'>('metric');

  // Notes state
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedNotesEntityId, setSelectedNotesEntityId] = useState<number | undefined>(undefined);
  const [selectedNotesEntityTitle, setSelectedNotesEntityTitle] = useState('');
  
  // Forensic flags dialog state
  const [forensicFlagsDialogOpen, setForensicFlagsDialogOpen] = useState(false);
  
  // Handle forensic flags loaded
  const handleForensicFlagsLoaded = (flagsData: Record<string, ForensicFlagData>) => {    
    // Update analysis tabs with forensic flags on each data point
    setAnalysisTabs(prevTabs => {
      const updatedTabs = prevTabs.map(tab => ({
        ...tab,
        data: tab.data.map((device: any) => {
          const deviceFlags = flagsData[device.registration_id];
          if (deviceFlags) {
            return {
              ...device,
              forensic_flags: deviceFlags
            };
          }
          return device;
        })
      }));
      return updatedTabs;
    });
    
  };

  const handleDeviceNotes = useCallback(async (registrationId: string, name?: string) => {
    // Strip "GN-" prefix if present - GeoNeutron API only accepts pure UUIDs
    const cleanId = registrationId.startsWith('GN-') ? registrationId.substring(3) : registrationId;
    const cleanName = name?.startsWith('GN-') ? name.substring(3) : name;
    
    try {
      // Check if device is in watchlist to get its ID
      const watchlist = await pathfinderApi.getWatchlist();
      // Search for both clean ID and original ID (in case it was saved with prefix)
      const savedDevice = watchlist.find(d => 
        d.registration_id === cleanId || d.registration_id === registrationId
      );
      
      let watchlistId = savedDevice?.id;
      
      if (!watchlistId) {
        if (window.confirm('This device must be saved to your watchlist to add notes. Save it now?')) {
          const saved = await pathfinderApi.addToWatchlist(cleanId, cleanName);
          watchlistId = saved.id;
          // Trigger refresh of watchlist in other components
          window.dispatchEvent(new CustomEvent('refresh-watchlist'));
        } else {
          return;
        }
      }
      
      if (watchlistId) {
        setSelectedNotesEntityId(watchlistId);
        setSelectedNotesEntityTitle(`Notes for ${cleanName || cleanId}`);
        setNotesDialogOpen(true);
      }
    } catch {
      alert('Failed to open notes. Please try again.');
    }
  }, []);

  // Get available devices for intel packet notes
  const availableDevicesForNotes = useMemo(() => {
    const devices: Array<{ id: string; label: string }> = [];
    const seenIds = new Set<string>();
    
    analysisTabs.forEach(tab => {
      // For PoL tabs, use the deviceId
      if (tab.type === 'pol' && tab.deviceId && !seenIds.has(tab.deviceId)) {
        seenIds.add(tab.deviceId);
        devices.push({ 
          id: tab.deviceId, 
          label: tab.label || tab.deviceId 
        });
      }
      // For other tabs, extract from data
      tab.data?.forEach((device: { registration_id?: string }) => {
        if (device.registration_id && !seenIds.has(device.registration_id)) {
          seenIds.add(device.registration_id);
          devices.push({ 
            id: device.registration_id, 
            label: device.registration_id 
          });
        }
      });
    });
    
    return devices;
  }, [analysisTabs]);

  // Save Overwatch article to device notes (intel packet)
  const handleSaveArticleToNotes = useCallback(async (article: OverwatchArticle): Promise<boolean> => {
    try {
      // Get the first available device, or prompt if multiple
      let targetDeviceId: string | undefined;
      let targetDeviceLabel: string | undefined;
      
      if (availableDevicesForNotes.length === 0) {
        alert('No devices loaded. Please load a device first to save intel.');
        return false;
      } else if (availableDevicesForNotes.length === 1) {
        targetDeviceId = availableDevicesForNotes[0].id;
        targetDeviceLabel = availableDevicesForNotes[0].label;
      } else {
        // Prompt user to select a device
        const deviceOptions = availableDevicesForNotes.map((d, i) => `${i + 1}. ${d.label}`).join('\n');
        const selection = window.prompt(
          `Select device to attach this intel to:\n\n${deviceOptions}\n\nEnter number (1-${availableDevicesForNotes.length}):`
        );
        if (!selection) return false;
        
        const index = parseInt(selection) - 1;
        if (isNaN(index) || index < 0 || index >= availableDevicesForNotes.length) {
          alert('Invalid selection');
          return false;
        }
        targetDeviceId = availableDevicesForNotes[index].id;
        targetDeviceLabel = availableDevicesForNotes[index].label;
      }
      
      if (!targetDeviceId) return false;
      
      // Strip "GN-" prefix if present
      const cleanId = targetDeviceId.startsWith('GN-') ? targetDeviceId.substring(3) : targetDeviceId;
      const cleanLabel = targetDeviceLabel?.startsWith('GN-') ? targetDeviceLabel.substring(3) : targetDeviceLabel;
      
      // Get or create watchlist entry
      const watchlist = await pathfinderApi.getWatchlist();
      let savedDevice = watchlist.find(d => 
        d.registration_id === cleanId || d.registration_id === targetDeviceId
      );
      
      let watchlistId = savedDevice?.id;
      
      if (!watchlistId) {
        // Auto-save device to watchlist for intel packet
        const saved = await pathfinderApi.addToWatchlist(cleanId, cleanLabel);
        watchlistId = saved.id;
        window.dispatchEvent(new CustomEvent('refresh-watchlist'));
      }
      
      // Format the article as a note
      const noteContent = `[OVERWATCH INTEL]
================================
**${article.title || 'Untitled Article'}**

Date: ${article.pubdate ? new Date(article.pubdate).toLocaleDateString('en-US', { 
  year: 'numeric', month: 'long', day: 'numeric' 
}) : 'Unknown'}
Category: ${article.category || article.primary_category || 'Uncategorized'}
${article.sentiment ? `Sentiment: ${article.sentiment}` : ''}
${article.latitude && article.longitude ? `Location: ${article.latitude.toFixed(4)}, ${article.longitude.toFixed(4)}${article.city || article.country ? ` (${[article.city, article.country].filter(Boolean).join(', ')})` : ''}` : ''}

Summary:
${article.summary || article.title || 'No summary available'}

${article.source_link ? `Source: ${article.source_link}` : ''}
================================
Added via Overwatch Intel on ${new Date().toLocaleDateString()}`;

      // Save the note
      await pathfinderApi.createNote(noteContent, watchlistId, undefined);
      
      return true;
    } catch (error) {
      console.error('Failed to save article to notes:', error);
      alert('Failed to save intel to notes. Please try again.');
      return false;
    }
  }, [availableDevicesForNotes]);

  // Toggle device visibility
  const handleToggleDeviceVisibility = useCallback((registrationId: string) => {
    setHiddenDevices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(registrationId)) {
        newSet.delete(registrationId);
      } else {
        newSet.add(registrationId);
      }
      return newSet;
    });
  }, []);

  // Focus on a single device - hide all others
  const handleFocusDevice = useCallback((registrationId: string, allDeviceIds: string[]) => {
    setHiddenDevices(prev => {
      const newSet = new Set<string>();
      // Check if we're already in "focus mode" for this device
      // (all others hidden, this one visible)
      const isAlreadyFocused = !prev.has(registrationId) && 
        allDeviceIds.filter(id => id !== registrationId).every(id => prev.has(id));
      
      if (isAlreadyFocused) {
        // Toggle off focus mode - show all devices
        return newSet; // Empty set = all visible
      } else {
        // Focus mode - hide all devices except the selected one
        allDeviceIds.forEach(id => {
          if (id !== registrationId) {
            newSet.add(id);
          }
        });
        return newSet;
      }
    });
  }, []);

  // Handle saving a registration ID to watchlist
  const handleSaveDevice = useCallback((registrationId: string) => {
  const cleanId = registrationId.startsWith('GN-') ? registrationId.substring(3) : registrationId;
  setPendingRegistrationId(cleanId);
  setWatchlistDeviceName(`Device ${cleanId.slice(0, 8)}`);
  setSaveDeviceDialogOpen(true);
}, []);

const handleConfirmSaveDevice = useCallback(async () => {
  if (!pendingRegistrationId) return;

  const finalName = watchlistDeviceName.trim() || `Device ${pendingRegistrationId.slice(0, 8)}`;
  setSaveDeviceLoading(true);

  try {
    await pathfinderApi.addToWatchlist(
      pendingRegistrationId,
      finalName,
      undefined,
      undefined,
      selectedCourse
    );

    window.dispatchEvent(new CustomEvent('refresh-watchlist'));
    setSaveDeviceDialogOpen(false);
    alert(`✅ Device "${finalName}" added to watchlist!`);
  } catch (error: any) {
    if (error.response?.status === 400) {
      alert('This device is already in your watchlist');
    } else {
      alert('Failed to save device. Check console for details.');
    }
  } finally {
    setSaveDeviceLoading(false);
  }
}, [pendingRegistrationId, watchlistDeviceName, selectedCourse]);

  // Left panel metadata (label only - component rendered separately to prevent remount)
  const leftPanelLabel = useMemo(() => {
    if (analysisTabs.length > 0) {
      const totalDevices = analysisTabs.reduce((acc, tab) => acc + tab.totalDevices, 0);
      return `Devices (${totalDevices})`;
    }
    return null;
  }, [analysisTabs]);
  
  // For backwards compat with existing code that checks leftTabs.length
  // Always show left panel so Ask Tracer is accessible
  const leftTabs = leftPanelLabel ? [{ label: leftPanelLabel }] : [{ label: 'Ask Tracer' }];

  // Dwell Sites state
  const [dwellSites, setDwellSites] = useState<DwellSite[]>([]);
  const [isEnrichingSites, setIsEnrichingSites] = useState(false);
  
  // Use shared enablePlaceNames from Zustand store (persists + syncs with Link Analysis)
  const enablePlaceNames = useTracerStore((state) => state.enablePlaceNames);
  const setEnablePlaceNames = useTracerStore((state) => state.setEnablePlaceNames);

  // Enrich dwell sites with geocoded place names when sites change (only if enabled)
  // Limit to top 100 sites by visit count to avoid excessive API calls
  useEffect(() => {
    const enrichSites = async () => {
      // Skip if geocoding is disabled
      if (!enablePlaceNames) return;
      
      // Only enrich if we have sites and they have generic names (Site A, Site B, etc.)
      if (dwellSites.length === 0) return;
      
      // Check if sites need enrichment (have generic "Site X" names)
      const needsEnrichment = dwellSites.some(site => /^Site [A-Z]/.test(site.name));
      if (!needsEnrichment) return;
      
      setIsEnrichingSites(true);
      try {
        // Sort sites by visit count (dwells.length) and limit to top 100 for geocoding
        const sortedSites = [...dwellSites].sort((a, b) => (b.dwells?.length || 0) - (a.dwells?.length || 0));
        const sitesToEnrich = sortedSites.slice(0, 100);
        const sitesNotEnriched = sortedSites.slice(100);
        
        const enrichedTopSites = await enrichSitesWithPlaceNames(sitesToEnrich);
        
        // Merge enriched sites back with non-enriched ones
        const enrichedSites = [...enrichedTopSites, ...sitesNotEnriched];
        
        // Only update if we got better names
        const hasNewNames = enrichedTopSites.some((site, i) => site.name !== sitesToEnrich[i]?.name);
        if (hasNewNames) {
          setDwellSites(enrichedSites);
          // Also update dwellLocations with the new site names
          const updatedDwells = updateDwellsWithSiteNames(dwellLocations as any[], enrichedSites);
          setDwellLocations(updatedDwells as any[]);
        }
      } catch {
        // Failed to enrich site names, use default names
      } finally {
        setIsEnrichingSites(false);
      }
    };

    enrichSites();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dwellSites.length, enablePlaceNames]); // Run when sites change OR when toggle is enabled

  // Helper to run dwell analysis
  const performDwellAnalysis = useCallback((points: PathfinderDataPoint[], params = { distance: 100, time: 300, radius: 150 }) => {
    const detectedDwells = detectDwells(points, params.distance, params.time);
    const { dwells: processedDwells, sites } = identifySites(detectedDwells, params.radius, points);
    
    return { dwells: processedDwells, sites };
  }, []);

  // Handle re-analysis from DwellAnalysis component
  const handleReanalyzeDwells = useCallback((params: { distanceThreshold: number, timeThreshold: number, siteRadius: number }) => {
    if (visibleDataPoints.length === 0) {
      alert('No data points available to analyze.');
      return;
    }
    
    setLoading(true);
    setLoadingMessage('Re-running dwell detection...');
    setTimeout(() => {
      try {
        const { dwells, sites } = performDwellAnalysis(visibleDataPoints, {
          distance: params.distanceThreshold,
          time: params.timeThreshold,
          radius: params.siteRadius
        });
        
        setDwellLocations(dwells);
        setDwellSites(sites);
      } catch (error) {
      } finally {
        setLoadingMessage('');
        setLoading(false);
      }
    }, 100);
  }, [visibleDataPoints, performDwellAnalysis]);

  // Filter dataPoints based on selectedDevices and hiddenDevices
  // NOTE: Must be defined BEFORE rightTabs since rightTabs uses these filtered values
  const filteredVisibleDataPoints = useMemo(() => {
    // Collect all selected device IDs
    const selectedDeviceIds = Object.values(selectedDevices).flat();
    
    // Start with all visible points
    let filtered = visibleDataPoints;
    
    // Filter out hidden devices (from eye icon toggle)
    if (hiddenDevices.size > 0) {
      filtered = filtered.filter(point => {
        // Check various ID formats that might be in hiddenDevices
        const deviceId = point.deviceId || '';
        const studentId = point.studentId || '';
        
        // hiddenDevices might contain: "GN-xxx", "xxx", or full registration ID
        const isHidden = hiddenDevices.has(deviceId) || 
                         hiddenDevices.has(studentId) ||
                         hiddenDevices.has(`GN-${deviceId}`) ||
                         hiddenDevices.has(studentId.replace('GN-', ''));
        
        return !isHidden;
      });
    }
    
    // If devices are explicitly selected (from checkbox), filter to only those
    if (selectedDeviceIds.length > 0) {
      filtered = filtered.filter(point => {
        if ((point as any).isCoTraveler) return true;
        // Use the human-readable format: "Student-XX-devicetype" (same as CompactTimeSlider)
        const deviceKey = `${point.studentId}-${(point.deviceType || 'phone').toLowerCase()}`;
        return selectedDeviceIds.includes(deviceKey);
      });
    }
    
    // Apply chart-based time filter (from Interactive Charts)
    if (chartFilter) {
      filtered = filtered.filter(point => {
        const date = new Date(point.timestamp);
        const pointHour = date.getHours();
        const pointDay = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
        
        if (chartFilter.type === 'hour') {
          return pointHour === chartFilter.hour;
        } else if (chartFilter.type === 'day') {
          return pointDay === chartFilter.dayIndex;
        } else if (chartFilter.type === 'hourDay') {
          return pointHour === chartFilter.hour && pointDay === chartFilter.dayIndex;
        }
        return true;
      });
    }
    
    return filtered;
  }, [visibleDataPoints, selectedDevices, hiddenDevices, chartFilter]);

  // Filter routes based on selectedDevices and hiddenDevices
  const filteredVisibleRoutes = useMemo(() => {
    const selectedDeviceIds = Object.values(selectedDevices).flat();
    
    let filtered = visibleRoutes;
    
    // Filter out hidden devices (from eye icon toggle)
    if (hiddenDevices.size > 0) {
      filtered = filtered.filter(route => {
        const deviceId = route.deviceId || '';
        const studentId = route.studentId || '';
        
        // Check various ID formats
        const isHidden = hiddenDevices.has(deviceId) || 
                         hiddenDevices.has(studentId) ||
                         hiddenDevices.has(`GN-${deviceId}`) ||
                         hiddenDevices.has(studentId.replace('GN-', ''));
        
        return !isHidden;
      });
    }
    
    // If devices are explicitly selected (from checkbox), filter to only those
    if (selectedDeviceIds.length > 0) {
      filtered = filtered.filter(route => {
        if ((route as any).isCoTravelerRoute) return true;
        // Route deviceId format is also usually "Student-07-phone"
        return selectedDeviceIds.includes(route.deviceId);
      });
    }
    
    // Apply chart-based time filter - filter route points and rebuild coordinates
    if (chartFilter) {
      filtered = filtered.map(route => {
        // Filter the route's points based on the chart filter
        const filteredPoints = route.points.filter(point => {
          const date = new Date(point.timestamp);
          const pointHour = date.getHours();
          const pointDay = date.getDay();
          
          if (chartFilter.type === 'hour') {
            return pointHour === chartFilter.hour;
          } else if (chartFilter.type === 'day') {
            return pointDay === chartFilter.dayIndex;
          } else if (chartFilter.type === 'hourDay') {
            return pointHour === chartFilter.hour && pointDay === chartFilter.dayIndex;
          }
          return true;
        });
        
        // Return route with filtered points and updated coordinates
        return {
          ...route,
          points: filteredPoints,
          coordinates: filteredPoints.map(p => p.coordinates),
        };
      }).filter(route => route.points.length > 0); // Remove routes with no matching points
    }
    
    return filtered;
  }, [visibleRoutes, selectedDevices, hiddenDevices, chartFilter]);

  // Filter dwell locations based on hiddenDevices
  const filteredDwellLocations = useMemo(() => {
    let filtered = dwellLocations;
    
    // Filter out hidden devices
    if (hiddenDevices.size > 0) {
      filtered = filtered.filter(dwell => {
        const entityId = dwell.entity_id || dwell.entityId || '';
        const deviceId = dwell.device_id || dwell.deviceId || '';
        
        // Check various ID formats
        const isHidden = hiddenDevices.has(entityId) || 
                         hiddenDevices.has(deviceId) ||
                         hiddenDevices.has(`GN-${entityId}`) ||
                         hiddenDevices.has(`GN-${deviceId}`) ||
                         hiddenDevices.has(entityId.replace('GN-', '')) ||
                         hiddenDevices.has(deviceId.replace('GN-', ''));
        
        return !isHidden;
      });
    }
    
    // Apply chart-based time filter
    if (chartFilter) {
      filtered = filtered.filter(dwell => {
        const startTime = new Date(dwell.start_time);
        const endTime = new Date(dwell.end_time);
        
        // For dwells, check if the filter time falls within the dwell period
        // or if the dwell spans the filtered time
        if (chartFilter.type === 'hour') {
          const startHour = startTime.getHours();
          const endHour = endTime.getHours();
          // Include if dwell spans the selected hour
          return startHour <= chartFilter.hour! && endHour >= chartFilter.hour!;
        } else if (chartFilter.type === 'day') {
          const startDay = startTime.getDay();
          const endDay = endTime.getDay();
          return startDay === chartFilter.dayIndex || endDay === chartFilter.dayIndex;
        } else if (chartFilter.type === 'hourDay') {
          const startHour = startTime.getHours();
          const startDay = startTime.getDay();
          const endHour = endTime.getHours();
          const endDay = endTime.getDay();
          return (startDay === chartFilter.dayIndex && startHour <= chartFilter.hour!) ||
                 (endDay === chartFilter.dayIndex && endHour >= chartFilter.hour!);
        }
        return true;
      });
    }
    
    return filtered;
  }, [dwellLocations, hiddenDevices, chartFilter]);

  // Right panel tabs (existing analysis tools)
  const rightTabs = useMemo(() => {
    
    return [
    { 
      label: 'Data Discovery', 
      icon: <AreaSearchIcon />, 
      component: (
        <AreaSearch
          selectedStudents={selectedStudents}
          dateRange={dateRange}
          timeRange={timeRange}
          onDrawModeChange={handleDrawModeChange}
          onCircleAdd={(circle) => {
            handleCircleAdd(circle);
          }}
          onCircleUpdate={handleCircleUpdate}
          onCircleDelete={handleCircleDelete}
          drawCircles={drawCircles}
          isDrawingMode={isDrawingMode}
          drawingShapeType={drawingShapeType}
          defaultRadius={defaultRadius}
          onRadiusChange={setDefaultRadius}
          measurementSystem={measurementSystem}
          onMeasurementSystemChange={setMeasurementSystem}
          editingCircleId={editingCircleId}
          onEditingCircleChange={setEditingCircleId}
          onZoomToLocation={handleZoomToLocation}
          onDateRangeChange={(newDateRange) => {
            setGlobalDateRange(newDateRange.start, newDateRange.end);
          }}
          onLoadAreaData={(areaDataPoints: PathfinderDataPoint[], geoNeutronResults?: any[], circleInfo?: { center: [number, number], radius: number }, exploreDeviceDetails?: Record<string, any>) => {            
            // Validate data points (coordinates are internal format: [lat, lng])
            const validatedDataPoints = areaDataPoints.map(point => {
              if (!point.coordinates || !Array.isArray(point.coordinates) || point.coordinates.length < 2) {
                return null;
              }
              const lat = parseFloat(String(point.coordinates[0])); // Internal: [0] is lat
              const lng = parseFloat(String(point.coordinates[1])); // Internal: [1] is lng
              if (isNaN(lat) || isNaN(lng)) return null;
              return { ...point, coordinates: [lat, lng] as [number, number] };  // Keep internal format
            }).filter(p => p !== null) as PathfinderDataPoint[];
            
            
            // Create a new area search tab if we have data points
            if (validatedDataPoints.length > 0) {
              
              // Count unique devices from the actual data points
              const uniqueDevices = new Set(validatedDataPoints.map((p: PathfinderDataPoint) => p.deviceId || p.studentId)).size;
              const tabId = `area-${Date.now()}`;
              
              // Generate routes for this tab's data
              const tabRoutes = pathfinderApi.generateRoutes(validatedDataPoints);
              
              // Store tab data in map FIRST
              setTabDataMap(prev => ({
                ...prev,
                [tabId]: {
                  dataPoints: validatedDataPoints,
                  routes: tabRoutes
                }
              }));
              
              // Note: We do NOT merge data anymore. Each tab is independent.
              // We set the visible data to THIS tab's data immediately.
              
              
              // Extract entity IDs and update selected students (to current tab only)
              const entityIds = Array.from(new Set(validatedDataPoints.map(p => p.studentId || p.deviceId)));
              setSelectedStudents(entityIds);
              
              // Update time range to include new data
              if (validatedDataPoints.length > 0) {
                const timestamps = validatedDataPoints.map((p: PathfinderDataPoint) => p.timestamp);
                const minTime = new Date(Math.min(...timestamps.map((t: Date) => t.getTime())));
                const maxTime = new Date(Math.max(...timestamps.map((t: Date) => t.getTime())));
                
                // Merge with existing time range if it exists
                const existingStart = timeRange?.start?.getTime() || minTime.getTime();
                const existingEnd = timeRange?.end?.getTime() || maxTime.getTime();
                
                setTimeRange({ 
                  start: new Date(Math.min(existingStart, minTime.getTime())), 
                  end: new Date(Math.max(existingEnd, maxTime.getTime()))
                });
                setCurrentTime(maxTime); // Set current time to end to show all data
              }
              
              // Create the tab
              const areaCount = analysisTabs.filter(tab => tab.label.startsWith('Area')).length;
              const tabLabel = `Area ${areaCount + 1}`;
              
              const rawTabData = geoNeutronResults || [];
              const tabData = rawTabData.map((d: any) => {
                const regId = (d.registration_id || '').replace(/^GN-/i, '');
                const explore = exploreDeviceDetails?.[regId];
                if (!explore) return d;
                return {
                  ...d,
                  browser_family: explore.userAgentBrowserFamily ?? null,
                  browser_version: explore.userAgentBrowserVersion ?? null,
                  os_family: explore.userAgentOSFamily ?? null,
                  os_version: normalizeOsVersion(explore.userAgentOSVersion),
                  device_family: explore.userAgentDeviceFamily ?? null,
                  device_brand: explore.userAgentDeviceBrand ?? null,
                  device_model: explore.userAgentDeviceModel ?? null,
                  advertiser_eu: explore.eu ?? null,
                  user_agent_full: explore.userAgentFull ?? null,
                  created_on: explore.createdOn ?? null,
                  updated_on: explore.updatedOn ?? null,
                };
              });
              
              const newTab = {
                id: tabId,
                label: tabLabel,
                data: tabData,
                totalDevices: uniqueDevices,
                timestamp: new Date().toISOString(),
                circleInfo: circleInfo,
                type: 'area' as const,
                areaDataPoints: validatedDataPoints, // Store raw data points for fallback
                areaRoutes: tabRoutes, // Store routes for fallback
                exploreDeviceDetails: exploreDeviceDetails ?? undefined,
              };
              
              // Update local state immediately
              setDataPoints(validatedDataPoints);
              setVisibleDataPoints(validatedDataPoints);
              setRoutes(tabRoutes);
              setVisibleRoutes(tabRoutes);
              
              // ⚠️ CRITICAL: Update TracerContext so map and Link Analysis page can access the data
              updateTracerState({ 
                dataPoints: validatedDataPoints,
                routes: tabRoutes 
              });
              
              // Update time range in TracerContext
              if (validatedDataPoints.length > 0) {
                const timestamps = validatedDataPoints.map((p: PathfinderDataPoint) => p.timestamp);
                const minTime = new Date(Math.min(...timestamps.map((t: Date) => t.getTime())));
                const maxTime = new Date(Math.max(...timestamps.map((t: Date) => t.getTime())));
                updateTracerState({ timeRange: { start: minTime, end: maxTime } });
              }
              
              // CRITICAL: Set zoom BEFORE adding the tab to ensure skipMapChangeRef is set
              // before the tab-switching effect runs
              if (circleInfo && handleZoomToLocation) {
                // Calculate appropriate zoom level based on radius
                let zoom = 14; // Default
                const radiusKm = circleInfo.radius / 1000;
                
                if (radiusKm > 50) zoom = 10;
                else if (radiusKm > 20) zoom = 11;
                else if (radiusKm > 10) zoom = 12;
                else if (radiusKm > 5) zoom = 13;
                else if (radiusKm > 2) zoom = 14;
                else if (radiusKm > 0.5) zoom = 14;
                else zoom = 15;
                
                // circleInfo.center is [lat, lng] format
                handleZoomToLocation(circleInfo.center[0], circleInfo.center[1], zoom);
              }
              
              addAnalysisTab(newTab);
              setShowDataPoints(true); // Ensure points are visible
              
              // Update the corresponding circle with the correct unique device count
              // Use functional update to get latest circle state (including aiSummary)
              if (circleInfo) {
                setDrawCircles(prev => {
                  const matchingCircle = prev.find(c => 
                    c.center[0] === circleInfo.center[0] && c.center[1] === circleInfo.center[1]
                  );
                  if (matchingCircle) {
                    // Create properly structured AreaSearchResult objects for unique devices
                    const uniqueDeviceIds = Array.from(new Set(validatedDataPoints.map(p => p.deviceId || p.studentId)));
                    const uniqueDeviceList = uniqueDeviceIds.map(id => {
                      const devicePoints = validatedDataPoints.filter(p => (p.deviceId || p.studentId) === id);
                      const timestamps = devicePoints.map(p => p.timestamp);
                      
                      return {
                        entityId: id,
                        entityType: 'device',
                        deviceType: devicePoints[0]?.deviceType || 'phone',
                        pointCount: devicePoints.length,
                        timeRange: {
                          start: new Date(Math.min(...timestamps.map(t => t.getTime()))),
                          end: new Date(Math.max(...timestamps.map(t => t.getTime())))
                        },
                        confidence: 1.0,
                        dataPoints: devicePoints
                      };
                    });
                    
                    
                    return prev.map(c => 
                      c.id === matchingCircle.id 
                        ? { ...c, results: uniqueDeviceList }  // Preserve all existing properties including aiSummary
                        : c
                    );
                  }
                  return prev;
                });
              }
              
              // Check infrastructure proximity in background (airports, seaports, borders)
              checkInfrastructureProximity(validatedDataPoints);
              
            } else {
            }
          }}
          onLoadPoLData={async (polDataPoints: PathfinderDataPoint[], circleInfo?: { center: [number, number], radius: number }, deviceId?: string) => {
            // Validate and prepare data points (coordinates in internal format: [latitude, longitude])
            const validatedDataPoints = polDataPoints.map(point => {
              if (!point.coordinates || !Array.isArray(point.coordinates) || point.coordinates.length < 2) {
                return null;
              }
              const lat = parseFloat(String(point.coordinates[0])); // Internal format: [0] is lat
              const lng = parseFloat(String(point.coordinates[1])); // Internal format: [1] is lng
              
              // Validate coordinates
              if (isNaN(lat) || isNaN(lng)) return null;
              
              // Sanity check: if lat is outside valid range, try swapping
              if (lat < -90 || lat > 90) {
                if (lng >= -90 && lng <= 90) {
                  return { ...point, coordinates: [lng, lat] as [number, number] }; // Swap to correct
                }
                return null;
              }
              
              if (lng < -180 || lng > 180) return null;
              
              return { ...point, coordinates: [lat, lng] as [number, number] }; // Keep internal format
            }).filter(p => p !== null) as PathfinderDataPoint[];
            
            // Create a new POL search tab
            
            // Convert data points to the format expected by the table
            const polDevices = validatedDataPoints.map((point: PathfinderDataPoint) => ({
              registration_id: point.deviceId,
              latitude: point.coordinates[0],  // Internal format: [lat, lng]
              longitude: point.coordinates[1],
              timestamp: point.timestamp.toISOString(),
              accuracy: point.accuracy || 0,
              source: 'geoneutron-pol',
              // Include top-level fields for table display
              ip_address: (point as any).ip_address || point.metadata?.ip_address,
              forensic_flag: (point as any).forensic_flag || point.metadata?.forensic_flag,
              country: (point as any).country || point.metadata?.country,
              carrier: (point as any).carrier || point.metadata?.carrier,
              timezone: (point as any).timezone || point.metadata?.timezone,
              altitude: (point as any).altitude || point.metadata?.altitude,
              speed: (point as any).speed || point.metadata?.speed,
              bearing: (point as any).bearing || point.metadata?.bearing,
              ...point.metadata
            }));

            // Fetch device details (Explore / device bulk API) for extra fields, same as area search
            let exploreDeviceDetails: Record<string, any> = {};
            try {
              const regIds = Array.from(new Set(validatedDataPoints.map(p => (p.studentId || p.deviceId || '').replace(/^GN-/i, '')))).filter(Boolean) as string[];
              if (regIds.length > 0) {
                const bulkRes = await geoneutronApi.getDevicesBulk(regIds);
                const devices = bulkRes?.devices ?? [];
                devices.forEach((d: any) => {
                  const id = (d.registrationID ?? d.registration_id ?? '').replace(/^GN-/i, '');
                  if (id) exploreDeviceDetails[id] = d;
                });
              }
            } catch {
              // Non-blocking
            }

            const tabData = polDevices.map((d: any) => {
              const regId = (d.registration_id || '').replace(/^GN-/i, '');
              const explore = exploreDeviceDetails?.[regId];
              if (!explore) return d;
              return {
                ...d,
                browser_family: explore.userAgentBrowserFamily ?? null,
                browser_version: explore.userAgentBrowserVersion ?? null,
                os_family: explore.userAgentOSFamily ?? null,
                os_version: normalizeOsVersion(explore.userAgentOSVersion),
                device_family: explore.userAgentDeviceFamily ?? null,
                device_brand: explore.userAgentDeviceBrand ?? null,
                device_model: explore.userAgentDeviceModel ?? null,
                advertiser_eu: explore.eu ?? null,
                user_agent_full: explore.userAgentFull ?? null,
                created_on: explore.createdOn ?? null,
                updated_on: explore.updatedOn ?? null,
              };
            });            
            
            const uniqueDevices = new Set(validatedDataPoints.map(p => p.deviceId)).size;
            
            // Safety check: ensure dwellLocations is an array
            const dwellsArray = Array.isArray(dwellLocations) ? dwellLocations : [];
            
            // Generate a unique tab ID
            const tabId = `pol-${Date.now()}`;
            
            // Generate routes for this tab's data
            const tabRoutes = pathfinderApi.generateRoutes(validatedDataPoints);
            
            // Run dwell analysis on the loaded PoL data to populate Story Mode
            const { dwells: polDwells, sites: polSites } = performDwellAnalysis(validatedDataPoints);
            
            // Merge with existing dwells (avoid duplicates)
            const existingIds = new Set(dwellsArray.map((d: any) => d.id));
            const newDwells = polDwells.filter((d: any) => !existingIds.has(d.id));
            const mergedDwells = [...dwellsArray, ...newDwells];
            setDwellLocations(mergedDwells);
            setVisibleDwells(mergedDwells);
            setDwellSites(polSites);
            
            // Store tab data in map FIRST
            setTabDataMap(prev => ({
              ...prev,
              [tabId]: {
                dataPoints: validatedDataPoints,
                routes: tabRoutes
              }
            }));
            
            // Update map data immediately for this tab
            setDataPoints(validatedDataPoints);
            setRoutes(tabRoutes);
            setVisibleDataPoints(validatedDataPoints);
            setVisibleRoutes(tabRoutes);
            
            // ⚠️ CRITICAL: Also update TracerContext so Link Analysis page can access the data
            updateTracerState({ 
              dataPoints: validatedDataPoints,
              routes: tabRoutes,
              dwellLocations: mergedDwells
            });
            
            
            // For PoL, REPLACE selection with just this device
            const entityIds = Array.from(new Set(validatedDataPoints.map(p => p.studentId || p.deviceId)));
            setSelectedStudents(entityIds);
            
            // Update time range to include new data
            if (validatedDataPoints.length > 0) {
              const timestamps = validatedDataPoints.map((p: PathfinderDataPoint) => p.timestamp);
              const minTime = new Date(Math.min(...timestamps.map((t: Date) => t.getTime())));
              const maxTime = new Date(Math.max(...timestamps.map((t: Date) => t.getTime())));
              
              // Merge with existing time range if it exists
              const existingStart = timeRange?.start?.getTime() || minTime.getTime();
              const existingEnd = timeRange?.end?.getTime() || maxTime.getTime();
              
              const newTimeRange = { 
                start: new Date(Math.min(existingStart, minTime.getTime())), 
                end: new Date(Math.max(existingEnd, maxTime.getTime()))
              };
              setTimeRange(newTimeRange);
              setCurrentTime(maxTime); // Set current time to end to show all data
              
              // ⚠️ CRITICAL: Also update TracerContext timeRange for Link Analysis
              updateTracerState({ timeRange: newTimeRange });
              
            }
            
            // Add tab with timeline data
            // If deviceId contains multiple IDs (comma-separated), find the source Area tab name
            const isMultiDevice = deviceId && deviceId.includes(',');
            let tabLabel: string;
            
            if (isMultiDevice) {
              // Find the source Area tab based on circleInfo
              const sourceAreaTab = analysisTabs.find(tab => 
                tab.type === 'area' && 
                tab.circleInfo?.center[0] === circleInfo?.center[0] &&
                tab.circleInfo?.center[1] === circleInfo?.center[1]
              );
              tabLabel = sourceAreaTab ? `${sourceAreaTab.label} PoL` : `PoL (${uniqueDevices} devices)`;
            } else if (deviceId) {
              // Single device - use short ID
              const shortId = deviceId.length > 8 ? deviceId.slice(0, 8) : deviceId;
              tabLabel = `PoL: ${shortId}`;
            } else {
              const polCount = analysisTabs.filter(tab => tab.label.startsWith('PoL')).length;
              tabLabel = `PoL ${polCount + 1}`;
            }
            
            const newTab = {
              id: tabId,
              label: tabLabel,
              data: tabData,
              totalDevices: uniqueDevices,
              timestamp: new Date().toISOString(),
              circleInfo: circleInfo,
              type: 'pol' as const,
              deviceId: deviceId,
              polDataPoints: validatedDataPoints,
              dwellLocations: polDwells,  // Use newly analyzed dwells, not empty deviceDwells
              exploreDeviceDetails: Object.keys(exploreDeviceDetails).length > 0 ? exploreDeviceDetails : undefined,
            };
            
            // CRITICAL: If circleInfo is available (from area search), zoom to that location BEFORE adding tab
            // This sets skipMapChangeRef to prevent the tab-switching effect from overriding
            if (circleInfo && handleZoomToLocation) {
              // Calculate appropriate zoom level based on radius
              let zoom = 14;
              const radiusKm = circleInfo.radius / 1000;
              
              if (radiusKm > 50) zoom = 10;
              else if (radiusKm > 20) zoom = 11;
              else if (radiusKm > 10) zoom = 12;
              else if (radiusKm > 5) zoom = 13;
              else if (radiusKm > 2) zoom = 14;
              else if (radiusKm > 0.5) zoom = 14;
              else zoom = 15;
              
              // circleInfo.center is [lat, lng] format
              handleZoomToLocation(circleInfo.center[0], circleInfo.center[1], zoom);
            }
            
            addAnalysisTab(newTab);
            
            // Check infrastructure proximity for PoL data (airports, seaports, borders)
            checkInfrastructureProximity(validatedDataPoints);
            
            // Fit map to extent of loaded PoL data - ONLY if no circleInfo (individual device PoL)
            // If circleInfo is present, we already zoomed to that location above
            if (validatedDataPoints.length > 0 && !circleInfo) {
              requestAnimationFrame(() => {
                setTimeout(() => {
                  if (!skipMapChangeRef.current) {
                    zoomToFitData(validatedDataPoints, polDwells);
                  }
                }, 100);
              });
            }
          }}
          onLoadAreaDwells={handleLoadAreaDwells}
        />
      )
    },
    {
      label: 'Story Mode',
      icon: <MenuBookIcon />,
      component: (
        <StoryMode
          dataPoints={filteredVisibleDataPoints}
          dwellLocations={filteredDwellLocations}
          currentTime={currentTime}
          selectedStudents={selectedStudents}
          infrastructureFindings={infrastructureFindings}
          onMapCenterChange={handleStoryModeMapChange}
          onTimeChange={handleStoryModeTimeChange}
        />
      )
    },
    { 
      label: 'Interactive Charts', 
      icon: <ChartIcon />, 
      component: (
        <InteractiveCharts
          dataPoints={filteredVisibleDataPoints}
          dwellLocations={filteredDwellLocations}
          coTravelerResults={coTravelerResults}
          studentColors={studentColors}
          onFilterChange={setChartFilter}
          activeFilter={chartFilter}
          enablePlaceNames={enablePlaceNames}
          setEnablePlaceNames={setEnablePlaceNames}
          isEnrichingSites={isEnrichingSites}
          onDwellClick={(lat, lon, _locationName) => {
            handleZoomToLocation(lat, lon, 15);
            setHighlightLocation({ lat, lng: lon });
          }}
        />
      )
    },
    { 
      label: 'Co-Traveler Analysis', 
      icon: <PeopleIcon />, 
      component: (
        <EnhancedCoTravelerAnalysis 
          dataPoints={dataPoints}
          selectedStudents={selectedStudents}
          selectedDevices={selectedDevices}
          coTravelerResults={coTravelerResults}
          coTravelerVisibility={coTravelerVisibility}
          onToggleCoTravelerVisibility={(resultKey, visible) => {
            setCoTravelerVisibility(prev => ({ ...prev, [resultKey]: visible }));
          }}
          onZoomToLocation={handleZoomToLocation}
          onLoadPolForDevice={(deviceId) => {
            const registrationId = (deviceId || '').replace(/^GN-/i, '').replace(/-(mobile|phone)$/i, '');
            if (registrationId) handleLoadDeviceData(registrationId);
          }}
          onVisualizationUpdate={async (results) => {
            setCoTravelerResults(results);
            
            // Initialize co-traveler visibility (default to visible)
            const newVisibility: Record<string, boolean> = {};
            results.forEach(result => {
              const resultKey = `${result.primaryDevice}-${result.coTravelerDevice}`;
              // Keep existing visibility if set, otherwise default to true (visible)
              newVisibility[resultKey] = coTravelerVisibility[resultKey] ?? true;
            });
            setCoTravelerVisibility(newVisibility);
            
            // Auto-generate Pattern of Life intel for co-traveler associations
            if (results.length > 0) {
              (async () => {
                try {
                  const intelResult = await generateAutoIntel(
                    [], // Dwells handled separately
                    results,
                    [],
                    { minCoTravelerEncounters: 3 }
                  );
                  if (intelResult.created > 0) {
                    console.log(`[Auto-Intel] Generated ${intelResult.created} co-traveler entries`);
                  }
                } catch (error) {
                  console.error('[Auto-Intel] Error generating co-traveler intel:', error);
                }
              })();
            }
            
            // STEP 2: Load COMPLETE GPS tracks for ALL identified co-travelers
            if (results.length > 0) {
              await loadCoTravelerTracks(results);
            } else {
            }
          }}
        />
      )
    },
    { 
      label: 'PoL Analysis', 
      icon: <DwellIcon />, 
      component: (
        <DwellAnalysis
          selectedStudents={selectedStudents}
          dwellLocations={filteredDwellLocations}
          sites={dwellSites}
          onReanalyze={handleReanalyzeDwells}
          onZoomToLocation={handleZoomToLocation}
          onResetView={handleResetMapView}
          enablePlaceNames={enablePlaceNames}
          setEnablePlaceNames={setEnablePlaceNames}
          isEnrichingSites={isEnrichingSites}
        />
      )
    },
    { 
      label: 'Overwatch', 
      icon: <OverwatchIcon sx={{ color: overwatchArticles.length > 0 ? '#ff9800' : undefined }} />, 
      component: (
        <OverwatchIntel
          dataPoints={dataPoints}
          dwellLocations={dwellLocations}
          onLoadArticlesToMap={handleLoadArticlesToMap}
          loadedArticles={overwatchArticles}
          onClearArticles={handleClearOverwatchArticles}
          onZoomToLocation={handleZoomToLocation}
          onSaveArticleToNotes={handleSaveArticleToNotes}
          availableDevices={availableDevicesForNotes}
          isFullPanel
        />
      )
    },
    { 
      label: 'Intel Packet', 
      icon: <IntelPacketIcon sx={{ color: theme.palette.warning.main }} />, 
      component: (
        <IntelPacket 
          isFullPanel 
          loadedDeviceIds={selectedStudents} 
          onZoomToLocation={handleZoomToLocation}
          mapBounds={mapBounds ? {
            north: mapBounds.north,
            south: mapBounds.south,
            east: mapBounds.east,
            west: mapBounds.west,
          } : undefined}
        />
      )
    },
    /*{ 
      label: 'Digital PoL', 
      icon: <PsychologyIcon />, 
      component: <DigitalPoL 
        selectedStudents={selectedStudents}
        timespan={14}
        onDigitalPoLDataChange={handleDigitalPoLDataChange}
      /> 
    },*/
  ];
  }, [
    visibleDataPoints,
    dwellLocations,
    coTravelerResults,
    studentColors,
    selectedStudents,
    dateRange,
    timeRange,
    handleDrawModeChange,
    handleCircleAdd,
    handleCircleUpdate,
    handleCircleDelete,
    drawCircles,
    isDrawingMode,
    drawingShapeType,
    defaultRadius,
    handleLoadAreaData,
    dataPoints,
    selectedDevices,
    coTravelerVisibility,
    loadCoTravelerTracks,
    currentTime,
    showDataPoints,
    showRouteLines,
    showHeatmap,
    showDwells,
    visibleRoutes,
    visibleDwells,
    handleStoryModeMapChange,
    handleStoryModeTimeChange,
    dwellSites,
    analysisTabs,
    removeAnalysisTab,
    leftTabs,
    overwatchArticles,
    handleLoadArticlesToMap,
    handleClearOverwatchArticles,
    handleZoomToLocation,
    handleSaveArticleToNotes,
    availableDevicesForNotes,
  ]);

  const activeTabForMap = analysisTabs.find(t => t.id === activeAnalysisTabId);

  return (
        <Box sx={{ 
      width: '100%',
      height: 'calc(100vh - 64px)', // Subtract header height
          display: 'flex', 
                  flexDirection: 'column',
        bgcolor: 'background.default',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Main Content Area - Style Layout */}
      <Box sx={{ 
        display: 'flex', 
        flex: 1, 
        minHeight: 0, // Critical: allows flex item to shrink below content size
        overflow: 'hidden' 
      }}>
        {/* Left Sidebar - Devices */}
        {leftTabs.length > 0 && (
      <Box 
            sx={{ 
            width: leftPanelCollapsed ? 48 : leftPanelWidth, 
            minWidth: leftPanelCollapsed ? 48 : leftPanelWidth,
            maxWidth: leftPanelCollapsed ? 48 : leftPanelWidth,
            flexShrink: 0,
            bgcolor: 'background.paper', 
            borderRight: 1,
            borderColor: 'divider',
        display: 'flex', 
        flexDirection: 'column', 
            position: 'relative',
            transition: (leftPanelCollapsed || isLeftResizing) ? 'none' : 'width 0.3s ease, min-width 0.3s ease, max-width 0.3s ease',
            overflow: 'hidden',
            // Stabilize layout during frequent re-renders (e.g., timeline playback)
            willChange: 'auto',
            transform: 'translateZ(0)', // Create stacking context for GPU acceleration
            backfaceVisibility: 'hidden',
          }}>
            {/* Left Panel Header */}
        <Box sx={{ 
              p: 1.5, 
              borderBottom: 1, 
              borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              {!leftPanelCollapsed && (
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {leftPanelLabel || 'Devices'}
                </Typography>
              )}
              <Tooltip 
                title={leftPanelCollapsed ? 'Expand Panel' : 'Collapse Panel'} 
                placement="right"
                arrow
              >
                <IconButton 
                  size="small"
                  onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
                  sx={{
                    bgcolor: theme.palette.mode === 'dark' 
                      ? `${theme.palette.primary.main}14` 
                      : `${theme.palette.primary.main}14`,
                    color: 'primary.main',
                    border: '1.5px solid',
                    borderColor: theme.palette.mode === 'dark' 
                      ? `${theme.palette.primary.main}80` 
                      : 'primary.main',
                    backdropFilter: 'blur(8px)',
                    '&:hover': {
                      bgcolor: 'primary.main',
                      color: 'white',
                      borderColor: 'primary.dark',
                      boxShadow: theme.palette.mode === 'dark'
                        ? `0 4px 12px ${theme.palette.primary.main}4D`
                        : `0 4px 12px ${theme.palette.primary.main}4D`,
                      transform: 'translateX(2px)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 2px 8px rgba(0, 0, 0, 0.4)'
                      : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    width: 32,
                    height: 32,
                  }}
                >
                  {leftPanelCollapsed ? <KeyboardDoubleArrowRight fontSize="small" /> : <KeyboardDoubleArrowLeft fontSize="small" />}
                </IconButton>
              </Tooltip>
      </Box>

            {/* Left Panel Content */}
            {!leftPanelCollapsed && (
        <Box sx={{ 
                flex: 1, 
                overflow: 'hidden', 
                overflowX: 'hidden',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
          display: 'flex',
                flexDirection: 'column',
                // Isolate layout recalculations to prevent shaking during playback
                contain: 'layout style',
                transform: 'translateZ(0)',
              }}>
                {/* Device List Sidebar with integrated Ask Tracer */}
                <DeviceListSidebar
                  tabs={analysisTabs}
                  activeTabId={activeAnalysisTabId}
                  onTabChange={setActiveAnalysisTabId}
                  onTabClose={(tabId) => {
                    removeAnalysisTab(tabId);
                  }}
                  onZoomToDeviceExtent={handleZoomToDeviceExtent}
                  onLoadDevice={handleLoadDeviceData}
                  onLoadAllDevices={handleLoadAllDevicesData}
                  onSaveDevice={handleSaveDevice}
                  onDeviceNotes={handleDeviceNotes}
                  onToggleDeviceVisibility={handleToggleDeviceVisibility}
                  onFocusDevice={handleFocusDevice}
                  hiddenDevices={hiddenDevices}
                  savedDeviceIds={savedDeviceIds}
                  savedDeviceNames={savedDeviceNames}
                  filterByMapBounds={filterByMapBounds}
                  onFilterByMapBoundsChange={setFilterByMapBounds}
                  mapBounds={mapBounds}
                  selectedCountries={selectedCountries}
                  selectedCarriers={selectedCarriers}
                  selectedTimezones={selectedTimezones}
                  selectedForensicFlags={selectedForensicFlags}
                  onCountriesChange={setSelectedCountries}
                  onCarriersChange={setSelectedCarriers}
                  onTimezonesChange={setSelectedTimezones}
                  onForensicFlagsChange={setSelectedForensicFlags}
                  // Ask Tracer props
                  showAskTracer={true}
                  askTracerProps={{
                    dataPoints,
                    dwellLocations,
                    onZoomToLocation: handleZoomToLocation,
                  }}
                  // Uploaded data management
                  onDeleteUploadedPoint={handleDeleteUploadedPoint}
                  onClearAllUploadedData={handleClearAllUploadedData}
                />
              </Box>
            )}
            
            {/* Left Panel Resize Handle */}
            {!leftPanelCollapsed && (
              <Box
                onMouseDown={() => setIsLeftResizing(true)}
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: 6,
                  cursor: 'ew-resize',
                  bgcolor: isLeftResizing ? 'primary.main' : 'transparent',
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    bgcolor: 'primary.main',
                    opacity: 0.5,
                  },
                  zIndex: 10,
                }}
              />
            )}
          </Box>
        )}

        {/* Main Map Area */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{ flex: 1, position: 'relative', width: '100%', height: '100%' }}>
          
          {/* SET8TimeSlider handles all playback functionality */}
          
          {loading && (
            <Box sx={{
              position: 'absolute',
              top: 10,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              bgcolor: 'background.paper',
              color: 'text.primary',
              padding: '10px 18px',
              borderRadius: 1,
              boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 300
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                  width: 16,
                  height: 16,
                  border: '2px solid #fff',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }} />
                <Typography variant="body2" sx={{ fontWeight: '500', fontSize: '0.875rem' }}>
                  {loadingMessage || 'Loading GPS Data...'}
                </Typography>
              </Box>
              {loadingMessage && loadingMessage.includes('GPS') && (
                <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem', textAlign: 'center' }}>
                  Large datasets may take 1-2 minutes
              </Typography>
              )}
            </Box>
          )}
        {/* Map Container with Style Selector */}
        <Box 
          sx={{ position: 'relative', width: '100%', height: '100%' }} 
          key={`map-${rightPanelCollapsed}-${leftPanelCollapsed}`}
        >
          <PathfinderMapGL
            dataPoints={filteredVisibleDataPoints.filter(point => {
              // Filter co-traveler points based on visibility toggles
              if ((point as any).isCoTraveler) {
                // Check if this co-traveler is visible
                const coTravelerEntity = point.studentId;
                // Find if any co-traveler result for this entity is visible
                const isVisible = coTravelerResults.some(result => {
                  if (result.coTravelerDevice === coTravelerEntity || 
                      result.coTravelerDevice.startsWith(coTravelerEntity)) {
                    const resultKey = `${result.primaryDevice}-${result.coTravelerDevice}`;
                    return coTravelerVisibility[resultKey] !== false;
                  }
                  return false;
                });
                return isVisible;
              }
              return true; // Always show primary subject points
            })}
            routes={filteredVisibleRoutes.filter(route => {
              // Filter co-traveler routes based on visibility toggles  
              if ((route as any).isCoTravelerRoute) {
                // Check if this co-traveler is visible
                const coTravelerEntity = route.studentId;
                // Find if any co-traveler result for this entity is visible
                const isVisible = coTravelerResults.some(result => {
                  if (result.coTravelerDevice === coTravelerEntity || 
                      result.coTravelerDevice.startsWith(coTravelerEntity)) {
                    const resultKey = `${result.primaryDevice}-${result.coTravelerDevice}`;
                    return coTravelerVisibility[resultKey] !== false;
                  }
                  return false;
                });
                return isVisible;
              }
              return true; // Always show primary subject routes
            })}
            studentColors={studentColors}
            mapSettings={{
              center: mapCenter,
              zoom: mapZoom,
              style: mapType
            }}
            showDataPoints={showDataPoints}
            showRouteLines={showRouteLines}
            showDirectionArrows={showDirectionArrows}
            showHeatmap={showHeatmap}
            extentUpdateTrigger={_extentUpdateTrigger}
            coTravelerResults={coTravelerResults.filter(result => {
              const resultKey = `${result.primaryDevice}-${result.coTravelerDevice}`;
              return coTravelerVisibility[resultKey] !== false; // Show if not explicitly hidden
            })}
            showCoTravelerPoints={true}
            showCoTravelerLines={false}
            showCoTravelerHeatmap={false}
            enableClustering={enableClustering}
            timeRange={timeRange}
            followMode={followMode}
            dwellLocations={visibleDwells}
            showDwells={showDwells}
            //digitalPoLData={showDigitalPoLOnMap ? digitalPoLData : []}
            //showDigitalPoL={showDigitalPoLOnMap}
            digitalPoLData={[]}
            showDigitalPoL={false}
            onMapChange={(center, zoom, bounds) => {
              // Skip updates during programmatic zoom animations
              if (skipMapChangeRef.current) return;
              setMapCenter(center);
              setMapZoom(zoom);
              // Update map bounds for geographic filtering
              if (bounds) {
                setMapBounds(bounds);
              }
            }}
            // Area search / circle drawing props
            isDrawingMode={isDrawingMode}
            drawingShapeType={drawingShapeType}
            drawCircles={drawCircles}
            defaultRadius={defaultRadius}
            measurementSystem={measurementSystem}
            onCircleDrawn={handleCircleDrawn}
            onCircleUpdate={handleCirclePartialUpdate}
            editingCircleId={editingCircleId}
            // Highlight location for zoom-to feature
            highlightLocation={highlightLocation}
            // Timeline slider visibility toggle
            timelineSliderHidden={timelineSliderHidden}
            onToggleTimelineSlider={() => setTimelineSliderHidden(!timelineSliderHidden)}
            // Location search callback - adds searched locations to uploaded data
            onLocationSearch={handleLocationSearch}
            // Overwatch articles
            overwatchArticles={overwatchArticles}
            showOverwatchArticles={overwatchArticles.length > 0}
            onArticleClick={handleOverwatchArticleClick}
            exploreDeviceDetails={(activeTabForMap as any)?.exploreDeviceDetails}
            showSatelliteLayer={showSatelliteLayer}
            onToggleSatelliteLayer={() => setShowSatelliteLayer((v) => !v)}
            satelliteGeoJson={satelliteGeoJson}
            satelliteTracksGeoJson={satelliteTracksGeoJson}
            satelliteLayerLoading={satelliteLayerLoading}
            onSatelliteTrackSelect={handleSatelliteTrackSelect}
            selectedSatelliteNorad={selectedSatelliteNorad}
          />
          
          {/* Compact Time Slider - Floats at top of map when data is loaded */}
          {dataPoints.length > 0 && !timelineSliderHidden && (
          <Box sx={{
              position: 'absolute',
              top: '20px',
              left: '20px',
              right: '60px', // Leave space for zoom controls
              maxWidth: '1200px',
              bgcolor: 'rgba(0, 0, 0, 0.85)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
              zIndex: 1001,
          }}>
            <CompactTimeSlider 
              dataPoints={dataPoints}
              routes={routes}
              timeRange={timeRange}
              selectedStudents={selectedStudents}
              selectedDevices={selectedDevices}
              dwellLocations={dwellLocations}
              onTimeUpdate={handleTimeUpdate}
              onFollowModeChange={handleFollowModeChange}
              // Visibility controls
              showDataPoints={showDataPoints}
              onShowDataPointsChange={setShowDataPoints}
              showRouteLines={showRouteLines}
              onShowRouteLinesChange={setShowRouteLines}
              showDirectionArrows={showDirectionArrows}
              onShowDirectionArrowsChange={setShowDirectionArrows}
              showHeatmap={showHeatmap}
              onShowHeatmapChange={setShowHeatmap}
              enableClustering={enableClustering}
              onEnableClusteringChange={setEnableClustering}
              showDwells={showDwells}
              onShowDwellsChange={setShowDwells}
              totalDataPoints={dataPoints.length}
              visibleDataPointsCount={visibleDataPoints.length}
            />
          </Box>
          )}
        </Box>
          </Box>
        </Box>

        {/* Right Sidebar - Analysis Tools */}
        <Box sx={{ 
          width: rightPanelCollapsed ? 48 : rightPanelWidth, 
          minWidth: rightPanelCollapsed ? 48 : rightPanelWidth,
          maxWidth: rightPanelCollapsed ? 48 : rightPanelWidth,
          flexShrink: 0,
          bgcolor: 'background.paper', 
          borderLeft: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          transition: 'width 0.3s ease, min-width 0.3s ease',
          overflow: 'hidden',
          height: '100%', // Explicit height for scaled displays
          minHeight: 0, // Allow shrinking in flex context
        }}>
          {/* Resize Handle */}
          {!rightPanelCollapsed && (
            <Box
              onMouseDown={handleMouseDown}
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 5,
                cursor: 'col-resize',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  opacity: 0.5,
                },
                zIndex: 10,
              }}
            />
          )}

          {/* Collapse/Expand Button */}
          <Box
            sx={{
              position: 'absolute',
              left: rightPanelCollapsed ? 8 : -14,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 20,
            }}
          >
            <Tooltip 
              title={rightPanelCollapsed ? 'Expand Tools Panel' : 'Collapse Tools Panel'} 
              placement="left"
              arrow
            >
              <IconButton
                onClick={handleRightPanelToggle}
                size="small"
                sx={{
                  bgcolor: theme.palette.mode === 'dark' 
                    ? `${theme.palette.primary.main}14` 
                    : `${theme.palette.primary.main}14`,
                  color: 'primary.main',
                  border: '1.5px solid',
                  borderColor: theme.palette.mode === 'dark' 
                    ? `${theme.palette.primary.main}80` 
                    : 'primary.main',
                  backdropFilter: 'blur(8px)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 3px 10px rgba(0, 0, 0, 0.5)'
                    : '0 3px 10px rgba(0, 0, 0, 0.15)',
                  '&:hover': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    borderColor: 'primary.dark',
                    boxShadow: theme.palette.mode === 'dark'
                      ? `0 6px 16px ${theme.palette.primary.main}66`
                      : `0 6px 16px ${theme.palette.primary.main}59`,
                    transform: 'translateX(-3px)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  width: 32,
                  height: 64,
                  borderRadius: '8px 0 0 8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                {rightPanelCollapsed ? (
                  <KeyboardDoubleArrowLeft fontSize="small" />
                ) : (
                  <KeyboardDoubleArrowRight fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>

          {!rightPanelCollapsed && (
            <>
              {/* Tool Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    '& .MuiTab-root': {
                      minHeight: 40,
                      fontSize: '0.8rem',
                      textTransform: 'none',
                    },
                  }}
                >
                  {rightTabs.map((tab, index) => (
                    <Tab
                      key={index}
                      icon={tab.icon}
                      label={tab.label}
                      iconPosition="start"
                      {...a11yProps(index)}
                      sx={{
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          '& .MuiSvgIcon-root': {
                            color: 'primary.contrastText',
                          },
                        },
                      }}
                    />
                  ))}
                </Tabs>
              </Box>

              {/* Tool Content */}
              <Box sx={{ flex: 1, overflow: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0, height: 0 }}>
                {rightTabs.map((tab, index) => (
                  <TabPanel key={index} value={activeTab} index={index}>
                    {tab.component}
                  </TabPanel>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Box>
        
      {/* GeoNeutron Data Table - Bottom Panel - Only show when tabs have actual data */}
      {analysisTabs.length > 0 && analysisTabs.some(tab => (tab.data?.length > 0 || tab.totalDevices > 0)) && (
        <GeoNeutronDataTable
          tabs={analysisTabs}
          activeTabId={activeAnalysisTabId}
          onTabChange={setActiveAnalysisTabId}
          isLoading={false}
          error={null}
          onZoomToDeviceExtent={handleZoomToDeviceExtent}
          onTabClose={(tabId) => {
            removeAnalysisTab(tabId);
          }}
          onLoadDevice={handleLoadDeviceData}
          onLoadAllDevices={handleLoadAllDevicesData}
          onSaveDevice={handleSaveDevice}
          onDeviceNotes={handleDeviceNotes}
          onToggleDeviceVisibility={handleToggleDeviceVisibility}
          onFocusDevice={handleFocusDevice}
          hiddenDevices={hiddenDevices}
          savedDeviceIds={savedDeviceIds}
          savedDeviceNames={savedDeviceNames}
          variant="bottom-panel"
          selectedCountries={selectedCountries}
          selectedCarriers={selectedCarriers}
          selectedTimezones={selectedTimezones}
          selectedForensicFlags={selectedForensicFlags}
          onCountriesChange={setSelectedCountries}
          onCarriersChange={setSelectedCarriers}
          onTimezonesChange={setSelectedTimezones}
          onForensicFlagsChange={setSelectedForensicFlags}
          filterByMapBounds={filterByMapBounds}
          onFilterByMapBoundsChange={setFilterByMapBounds}
          mapBounds={mapBounds}
          onDeleteUploadedPoint={handleDeleteUploadedPoint}
          onClearAllUploadedData={handleClearAllUploadedData}
        />
      )}
      
      {/* Notes Dialog */}
      <NotesDialog
        open={notesDialogOpen}
        onClose={() => setNotesDialogOpen(false)}
        title={selectedNotesEntityTitle}
        watchlistId={selectedNotesEntityId}
      />
      
      {/* Forensic Flags Dialog - uses active tab devices and current time range */}
      <ForensicFlagsDialog
        open={forensicFlagsDialogOpen}
        onClose={() => setForensicFlagsDialogOpen(false)}
        devices={
          // Only pass devices from the active tab
          analysisTabs.find(tab => tab.id === activeAnalysisTabId)?.data || []
        }
        dateRange={timeRange || { start: new Date(), end: new Date() }}
        activeTabLabel={analysisTabs.find(tab => tab.id === activeAnalysisTabId)?.label || 'Unknown'}
        onFlagsLoaded={handleForensicFlagsLoaded}
      />
      <Dialog
        open={saveDeviceDialogOpen}
        onClose={() => !saveDeviceLoading && setSaveDeviceDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Name this device</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            label="Watchlist name"
            value={watchlistDeviceName}
            onChange={(e) => setWatchlistDeviceName(e.target.value)}
            disabled={saveDeviceLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleConfirmSaveDevice();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDeviceDialogOpen(false)} disabled={saveDeviceLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirmSaveDevice} disabled={saveDeviceLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* Infrastructure Alert Snackbar - Centered on map */}
      <Snackbar 
        open={infrastructureAlert.open} 
        autoHideDuration={15000}
        onClose={() => setInfrastructureAlert(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          top: '50% !important',
          transform: 'translateY(-50%)',
          left: '50% !important',
          right: 'auto !important',
          '& .MuiSnackbar-root': {
            position: 'fixed'
          }
        }}
      >
        <Alert 
          severity="warning"
          onClose={() => setInfrastructureAlert(prev => ({ ...prev, open: false }))}
          sx={{ 
            width: '100%', 
            minWidth: 400,
            maxWidth: 500,
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            border: '2px solid #ff9800',
            '& .MuiAlert-message': { width: '100%' }
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {infrastructureAlert.message}
          </Typography>
          {infrastructureAlert.details.map((detail, idx) => (
            <Typography key={idx} variant="body2" sx={{ fontSize: '0.85rem' }}>
              {detail}
            </Typography>
          ))}
        </Alert>
      </Snackbar>
      
      {/* PoL Loading Snackbar */}
      <Snackbar 
        open={polLoading} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={polLoadingMessage.startsWith('✅') ? 'success' : 'info'} 
          icon={polLoadingMessage.startsWith('✅') ? undefined : <CircularProgress size={20} />}
          sx={{ width: '100%' }}
        >
          {polLoadingMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PathfinderPage;
